import React, { useState, useEffect, useMemo, useRef } from 'react'
import { PageHeader, Table, Button, Space, Modal, Form, Row, Col, Progress, Input, Select, Radio, Divider, Tooltip, Switch, InputNumber, Card, Alert, Checkbox, Spin, Tabs } from 'antd'
import { EditOutlined, InfoCircleOutlined, InboxOutlined, EyeOutlined } from '@ant-design/icons'
import { Box } from 'atom/box'
import { BackgroundContainer } from 'atom/layout/backgroundContainer'
import { AlertError } from 'components/error'
import { withAuthenticatedLayout } from 'components/layouts/layout'
import { useFormatMessage } from 'helpers/intl'
import { plusSVG } from 'assets/svg/plus'
import { QuestionIcon } from 'assets/svg/questionMark'
import { ButtonIcon } from 'atom/button'
import { magnifyBlackSVG } from 'assets/svg/magnify'
import { theme } from 'theme'
import { debounce } from 'lodash'
import { useTaxConfigurations, TaxRule } from 'hooks/useTaxConfigurations'
import { CreateTaxConfiguration, UpdateTaxConfiguration, GetTaxConfigurationById, SearchTaxNames, SearchZipCodes } from 'services/data-provider/tax'
import { useNotifications } from 'hooks/useNotification'
import { SwtchApiError } from 'models/error'
import { styled } from 'theme'

const { Option } = Select

const STATE_OPTIONS = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
]

const STATE_LABEL_MAP: Record<string, string> = STATE_OPTIONS.reduce((acc, state) => {
  acc[state.value] = state.label
  return acc
}, {} as Record<string, string>)

const SearchBarContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: ${theme.colors.white};
  border-radius: 48px;
  padding: 0 !important;
  overflow: hidden;
  gap: 0 !important;
  flex: 0 0 auto;
  width: 66.67%;
  box-shadow: none !important;
  outline: none !important;
  
  .ant-select-item-option-selected .ant-select-item-option-state {
    display: none !important;
  }
  
  /* Make checkbox non-interactive so clicking text selects the option */
  .ant-select-dropdown .ant-select-item-option .ant-checkbox-wrapper {
    pointer-events: none;
  }
  
  .ant-select-dropdown .ant-select-item-option .ant-checkbox {
    pointer-events: none;
  }
  
  .ant-select-dropdown .ant-select-item-option .ant-checkbox-inner {
    pointer-events: none;
  }
  
  /* Remove checkmark icon from selected tags, but keep the text visible */
  /* Hide the checkbox input and inner checkbox, but keep the wrapper and text */
  .ant-select-selection-item .ant-checkbox,
  .ant-select-selection-item .ant-checkbox-input,
  .ant-select-selection-item .ant-checkbox-inner {
    display: none !important;
    visibility: hidden !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    opacity: 0 !important;
  }
  
  /* Keep the checkbox wrapper visible but remove spacing for the hidden checkbox */
  .ant-select-selection-item .ant-checkbox-wrapper {
    display: inline-block !important;
    visibility: visible !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  /* Ensure text content is visible and properly displayed */
  .ant-select-selection-item {
    display: inline-flex !important;
    align-items: center !important;
    padding: 8px 8px !important;
    height: auto !important;
    min-height: 24px !important;
  }
  
  .ant-select-selection-item .ant-select-selection-item-content {
    display: inline-flex !important;
    align-items: center !important;
    visibility: visible !important;
    opacity: 1 !important;
    width: auto !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1.2 !important;
  }
  
  /* Make sure the text span inside checkbox wrapper is visible */
  .ant-select-selection-item .ant-checkbox-wrapper {
    display: inline-flex !important;
    align-items: center !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  .ant-select-selection-item .ant-checkbox-wrapper > span:not(.ant-checkbox):not(.ant-checkbox-inner) {
    display: inline !important;
    visibility: visible !important;
    margin-left: 0 !important;
    line-height: 1.2 !important;
  }
  
  .ant-select,
  .ant-input {
    margin-right: 0 !important;
    margin-left: -1px !important;
    height: 39px;
    position: relative;
    
    &:first-child {
      margin-left: 0 !important;
    }
    
    .ant-select-selector,
    &.ant-input {
      height: 39px;
      line-height: 12px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
      text-align: left !important;
      border: 1px solid ${theme.colors.lightGray} !important;
    }
    
    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      line-height: 12px !important;
      height: 39px !important;
      display: flex !important;
      align-items: center !important;
      text-align: left !important;
    }
    
    .ant-select-selection-search {
      left: 4px !important;
      height: 37px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
    }
    
    .ant-select-selection-search-input {
      height: 22px !important;
      line-height: 12px !important;
      display: flex !important;
      align-items: center !important;
      text-align: left !important;
    }
    
    .ant-select-selection-item {
      line-height: 12px !important;
      height: 34px !important;
      margin-top: 0 !important;
      margin-bottom: 0 !important;
      display: inline-flex !important;
      align-items: center !important;
      text-align: left !important;
    }
    
    .ant-select-selection-overflow {
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
      height: 34px !important;
      margin-left: 3px !important;
    }
    
    .ant-select-selection-overflow-item {
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
    }
    
    input {
      text-align: left !important;
    }

    &:hover {
      z-index: 2;
      .ant-select-selector, &.ant-input {
        border-color: ${theme.colors.lightGray} !important;
      }
    }

    &.ant-select-focused, &:focus-within, &.ant-input-focused {
      z-index: 3;
      .ant-select-selector, &.ant-input {
        border-color: ${theme.colors.primary} !important;
      }
    }
  }
  
  .ant-select {
    margin-right: 0 !important;
    
    &:first-child .ant-select-selector {
      border-radius: 48px 0 0 48px !important;
    }
    
    &:last-child .ant-select-selector {
      border-radius: 0 !important;
    }
    
    &:not(:first-child):not(:last-child) .ant-select-selector {
      border-radius: 0 !important;
    }
  }

  .rounded-select {
    margin-right: 0 !important;
    margin-left: -1px !important;
    
    .ant-select-selector {
      border-radius: 0 !important;
      border: 1px solid ${theme.colors.lightGray} !important;
    }
    
    &:first-child .ant-select-selector {
      border-radius: 48px 0 0 48px !important;
    }
    
    &:not(:first-child) {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    &.ant-select-focused, &:focus-within {
      z-index: 3;
      .ant-select-selector {
        border-color: ${theme.colors.primary} !important;
      }
    }
  }
  
  .ant-btn {
    border-radius: 0 48px 48px 0 !important;
    margin-left: -1px !important;
    height: 39px;
    background-color: ${theme.colors.neonGreen} !important;
    border: 1px solid ${theme.colors.lightGray} !important;
    border-left: none !important;
    color: ${theme.colors.black} !important;
  }
`

const TaxTableContainer = styled.div`
  .tax-action-icon-button.ant-btn-link,
  .tax-action-icon-button.ant-btn-link:hover,
  .tax-action-icon-button.ant-btn-link:focus,
  .tax-action-icon-button.ant-btn-link:active {
    color: ${theme.colors.grey7 || '#262626'} !important;
  }
  
  .tax-action-icon-button.ant-btn-link[disabled],
  .tax-action-icon-button.ant-btn-link[disabled]:hover {
    color: ${theme.colors.grey5 || '#d9d9d9'} !important;
    opacity: 0.5;
  }
  
  .tax-action-icon-button.ant-btn-link svg,
  .tax-action-icon-button.ant-btn-link svg path {
    color: ${theme.colors.grey7 || '#262626'} !important;
    fill: ${theme.colors.grey7 || '#262626'} !important;
    stroke: ${theme.colors.grey7 || '#262626'} !important;
  }
  
  .tax-action-icon-button.ant-btn-link[disabled] svg,
  .tax-action-icon-button.ant-btn-link[disabled] svg path {
    color: ${theme.colors.grey5 || '#d9d9d9'} !important;
    fill: ${theme.colors.grey5 || '#d9d9d9'} !important;
    stroke: ${theme.colors.grey5 || '#d9d9d9'} !important;
  }
`

const ActionIconButton = styled(Button)`
  &.tax-action-icon-button {
    color: ${theme.colors.grey7 || '#262626'} !important;
    
    &:hover,
    &:focus,
    &:active,
    &:visited {
      color: ${theme.colors.grey7 || '#262626'} !important;
    }
    
    &[disabled],
    &[disabled]:hover,
    &[disabled]:focus {
      color: ${theme.colors.grey5 || '#d9d9d9'} !important;
      opacity: 0.5;
    }
    
    svg,
    svg path,
    svg circle,
    svg polygon,
    svg polyline {
      color: ${theme.colors.grey7 || '#262626'} !important;
      fill: ${theme.colors.grey7 || '#262626'} !important;
      stroke: ${theme.colors.grey7 || '#262626'} !important;
    }
    
    &[disabled] svg,
    &[disabled] svg path,
    &[disabled] svg circle,
    &[disabled] svg polygon,
    &[disabled] svg polyline {
      color: ${theme.colors.grey5 || '#d9d9d9'} !important;
      fill: ${theme.colors.grey5 || '#d9d9d9'} !important;
      stroke: ${theme.colors.grey5 || '#d9d9d9'} !important;
    }
  }
`

const TaxBasePage: React.FC = () => {
  document.querySelector('body')?.classList.add('redesignActive')

  const { taxRules, pagination, loading, error, sortByLastUpdated, filters, handlePaginationChange, fetchTaxConfigurations, handleSortChange, handleFilterChange, handleClearFilters } = useTaxConfigurations()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isArchiveConfirmModalOpen, setIsArchiveConfirmModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [recordToView, setRecordToView] = useState<TaxRule | null>(null)
  const [recordToArchive, setRecordToArchive] = useState<TaxRule | null>(null)
  const [form] = Form.useForm()
  const [taxNameValue, setTaxNameValue] = useState<string>('')
  const [taxDisplayNameValue, setTaxDisplayNameValue] = useState<string>('')
  const [displayNotesValue, setDisplayNotesValue] = useState<string>('')
  const [locationSpecification, setLocationSpecification] = useState('byState')
  const [savedFormState, setSavedFormState] = useState<any>(null)
  const [filterTaxName, setFilterTaxName] = useState<string[]>([])
  const [filterLocationType, setFilterLocationType] = useState<'state' | 'zip' | undefined>(undefined)
  const [filterLocation, setFilterLocation] = useState<string[]>([])
  const [filterStatus, setFilterStatus] = useState<('active' | 'archive' | 'draft')[]>([])
  const [taxNameOptions, setTaxNameOptions] = useState<Array<{ value: string; label: string }>>([])
  const [zipCodeOptions, setZipCodeOptions] = useState<Array<{ value: string; label: string }>>([])
  const [fetchingTaxNames, setFetchingTaxNames] = useState(false)
  const [fetchingZipCodes, setFetchingZipCodes] = useState(false)
  const taxNameFetchRef = useRef(0)
  const zipCodeFetchRef = useRef(0)

  // Sync local filter state with filters prop
  useEffect(() => {
    setFilterTaxName(Array.isArray(filters.taxName) ? filters.taxName : filters.taxName ? [filters.taxName] : [])
    setFilterLocationType(filters.locationType)
    setFilterLocation(Array.isArray(filters.location) ? filters.location : filters.location ? [filters.location] : [])
    // Map backend "inactive" to frontend "archive"
    const mapStatusFromBackend = (status: 'active' | 'inactive' | 'draft'): 'active' | 'archive' | 'draft' => 
      status === 'inactive' ? 'archive' : status
    const statusArray: ('active' | 'inactive' | 'draft')[] = Array.isArray(filters.status) 
      ? filters.status 
      : filters.status 
        ? [filters.status] 
        : []
    setFilterStatus(statusArray.map(mapStatusFromBackend))
  }, [filters])

  // Debounced search for tax names
  const debouncedSearchTaxNames = useMemo(() => {
    const loadOptions = (value: string) => {
      if (!value || value.length < 2) {
        setTaxNameOptions([])
        return
      }
      taxNameFetchRef.current += 1
      const fetchId = taxNameFetchRef.current
      setFetchingTaxNames(true)
      setTaxNameOptions([])

      SearchTaxNames(value)
        .then((taxNames) => {
          if (fetchId !== taxNameFetchRef.current) {
            return
          }
          const opts = taxNames.map((name) => ({
            value: name,
            label: name,
          }))
          setTaxNameOptions(opts)
          setFetchingTaxNames(false)
        })
        .catch(() => {
          if (fetchId !== taxNameFetchRef.current) {
            return
          }
          setTaxNameOptions([])
          setFetchingTaxNames(false)
        })
    }
    return debounce(loadOptions, 800)
  }, [])

  // Debounced search for zip codes
  const debouncedSearchZipCodes = useMemo(() => {
    const loadOptions = (value: string) => {
      if (!value || value.length < 2) {
        setZipCodeOptions([])
        return
      }
      zipCodeFetchRef.current += 1
      const fetchId = zipCodeFetchRef.current
      setFetchingZipCodes(true)
      setZipCodeOptions([])

      SearchZipCodes(value)
        .then((zipCodes) => {
          if (fetchId !== zipCodeFetchRef.current) {
            return
          }
          const opts = zipCodes.map((item) => ({
            value: item.zip_code,
            label: item.zip_code,
          }))
          setZipCodeOptions(opts)
          setFetchingZipCodes(false)
        })
        .catch(() => {
          if (fetchId !== zipCodeFetchRef.current) {
            return
          }
          setZipCodeOptions([])
          setFetchingZipCodes(false)
        })
    }
    return debounce(loadOptions, 800)
  }, [])

  const [powerThresholdEnabled, setPowerThresholdEnabled] = useState(false)
  const [minimumTransactionAmountEnabled, setMinimumTransactionAmountEnabled] = useState(false)
  const [rateType, setRateType] = useState('perKwh')
  const [applyTaxToFreeSessions, setApplyTaxToFreeSessions] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<any>(undefined)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [loadingRecord, setLoadingRecord] = useState(false)
  const { openErrorNotification, openSuccessNotification } = useNotifications()

  // All format messages must be called at the top level
  const taxComplianceDescriptionText = useFormatMessage(
    'tax.taxComplianceDescription',
    'These tax rules apply to EV charging transactions. Taxes are charged only to the driver who pays for each session.\n Active tax rules will appear in Pricing Plans for affected sites based on the tax rule location. To apply a tax rule to site listings, refer to the Pricing Plans section.',
  )
  const taxNameText = useFormatMessage('tax.taxName', 'Tax Name')
  const taxDescriptionText = useFormatMessage('tax.taxDescription', 'Tax Description')
  const locationText = useFormatMessage('tax.location', 'Location')
  const rateSummaryColumnText = useFormatMessage('tax.rateSummaryColumn', 'Rate Summary')
  const powerRangeText = useFormatMessage('tax.powerRange', 'Power Range')
  const statusText = useFormatMessage('tax.status', 'Status')
  const activeText = useFormatMessage('tax.active', 'Active')
  const archiveText = useFormatMessage('tax.archive', 'Archive')
  const draftText = useFormatMessage('tax.draft', 'Draft')
  const lastUpdatedText = useFormatMessage('tax.lastUpdated', 'Last Updated')
  const actionsText = useFormatMessage('tax.actions', 'Actions')
  const sortByText = useFormatMessage('tax.sortBy', 'Sort By')
  const sortDescText = useFormatMessage('tax.sortDesc', 'Newest First')
  const sortAscText = useFormatMessage('tax.sortAsc', 'Oldest First')
  const sortNoneText = useFormatMessage('tax.sortNone', 'No Sort')
  const filterTaxNameText = useFormatMessage('tax.filterTaxName', 'Tax Name')
  const filterLocationTypeText = useFormatMessage('tax.filterLocationType', 'Location Type')
  const filterLocationText = useFormatMessage('tax.filterLocation', 'Location')
  const filterStatusText = useFormatMessage('tax.filterStatus', 'Status')
  const clearFiltersText = useFormatMessage('tax.clearFilters', 'Clear Filters')
  const stateTextFilter = useFormatMessage('tax.state', 'State')
  const zipCodeTextFilter = useFormatMessage('tax.zipCode', 'ZIP Code')
  const newTaxRuleText = useFormatMessage('tax.newTaxRule', 'NEW TAX RULE')
  const exciseTaxText = useFormatMessage('tax.exciseTax', 'Excise Tax')
  const createTaxRuleText = useFormatMessage('tax.createTaxRule', 'Create Tax Rule')
  const cancelText = useFormatMessage('tax.cancel', 'Cancel')
  const saveAsDraftText = useFormatMessage('tax.saveAsDraft', 'Save as Draft')
  const saveAndActivateText = useFormatMessage('tax.saveAndActivate', 'Save and Activate')
  const formCompletionText = useFormatMessage('tax.formCompletion', 'Form Completion')
  const ruleBasicsText = useFormatMessage('tax.ruleBasics', 'Rule Basics')
  const taxTypeText = useFormatMessage('tax.taxType', 'Tax Type')
  const enterNameText = useFormatMessage('tax.enterName', 'Enter a name')
  const enterDescriptionText = useFormatMessage('tax.enterDescription', 'Enter a description')
  const countryText = useFormatMessage('tax.country', 'Country')
  const unitedStatesText = useFormatMessage('tax.unitedStates', 'United States')
  const locationSpecificationText = useFormatMessage('tax.locationSpecification', 'Location Specification')
  const byStateText = useFormatMessage('tax.byState', 'By State')
  const byZipCodesText = useFormatMessage('tax.byZipCodes', 'By ZIP Codes')
  const statesText = useFormatMessage('tax.states', 'States')
  const selectStatesText = useFormatMessage('tax.selectStates', 'Select states')
  const zipCodesText = useFormatMessage('tax.zipCodes', 'ZIP Codes')
  const enterZipCodesText = useFormatMessage('tax.enterZipCodes', 'Enter ZIP codes separated by commas (e.g., 10001, 10002, 10003)')
  const notSpecifiedText = useFormatMessage('tax.notSpecified', 'Not specified')
  const taxDisplayNameText = useFormatMessage('tax.taxDisplayName', 'Tax Display Name')
  const taxDisplayNamePlaceholderText = useFormatMessage('tax.taxDisplayNamePlaceholder', 'Ex: HST')
  const taxDisplayNameTooltipText = useFormatMessage('tax.taxDisplayNameTooltip', 'Tax Display Name will appear as is on the driver receipt')
  const displayNotesText = useFormatMessage('tax.displayNotes', 'Display Notes')
  const displayNotesPlaceholderText = useFormatMessage('tax.displayNotesPlaceholder', 'Enter display notes')
  const displayNotesTooltipText = useFormatMessage('tax.displayNotesTooltip', 'Describe the tax for the driver\'s knowledge on the receipt')
  const taxAuthorityContactText = useFormatMessage('tax.taxAuthorityContact', 'Tax Authority Contact')
  const taxAuthorityContactPlaceholderText = useFormatMessage('tax.taxAuthorityContactPlaceholder', 'Contact info or link')
  const regulatoryReferenceText = useFormatMessage('tax.regulatoryReference', 'Regulatory Reference')
  const regulatoryReferencePlaceholderText = useFormatMessage('tax.regulatoryReferencePlaceholder', 'URL to official documentation')
  const taxConditionText = useFormatMessage('tax.taxCondition', 'Tax Condition')
  const powerThresholdText = useFormatMessage('tax.powerThreshold', 'Power Threshold in kW')
  const powerThresholdHelperText = useFormatMessage('tax.powerThresholdHelper', 'Apply to chargers within a power range (e.g., between 0 kW and 50 kW)')
  const minimumTransactionAmountText = useFormatMessage('tax.minimumTransactionAmount', 'Minimum Transaction Amount')
  const minimumTransactionAmountHelperText = useFormatMessage('tax.minimumTransactionAmountHelper', 'Tax only applies if total exceeds this amount')
  const ratesAndCalculationText = useFormatMessage('tax.ratesAndCalculation', 'Rates & Calculation')
  const rateTypeText = useFormatMessage('tax.rateType', 'Rate Type')
  const perKwhDollarText = useFormatMessage('tax.perKwhDollar', 'Per kWh')
  const percentageText = useFormatMessage('tax.percentage', 'Percentage (%)')
  const taxAppliesToText = useFormatMessage('tax.taxAppliesTo', 'Tax Applies To')
  const allCostText = useFormatMessage('tax.allCost', 'All Cost')
  const energyCostOnlyText = useFormatMessage('tax.energyCostOnly', 'Energy Cost Only')
  const rateValueHelperText = useFormatMessage('tax.rateValueHelper', 'Tax rate per kilowatt-hour (up to 6 decimal places)')
  const ratePercentageLabelText = useFormatMessage('tax.ratePercentageLabel', 'Rate as Percentage')
  const ratePerKwhLabelText = useFormatMessage('tax.ratePerKwhLabel', 'Rate per kWh')
  const applyTaxToFreeSessionsText = useFormatMessage('tax.applyTaxToFreeSessions', 'Exempt Free\nSessions\nfrom Tax')
  const applyTaxToFreeSessionsHelperTextOn = useFormatMessage('tax.applyTaxToFreeSessionsHelperOn', 'Tax will be calculated and charged even when the session cost is $0.00')
  const applyTaxToFreeSessionsHelperTextOff = useFormatMessage('tax.applyTaxToFreeSessionsHelperOff', 'No tax will be charged when the session cost is $0.00')
  const applyTaxToFreeSessionsNoteText = useFormatMessage('tax.applyTaxToFreeSessionsNote', 'Note: Some states require tax on free sessions (e.g., IA, KY) while others exempt them (e.g., UT)')
  const ruleSummaryText = useFormatMessage('tax.ruleSummary', 'Rule Summary')
  const taxConditionsText = useFormatMessage('tax.taxConditions', 'Tax Conditions')
  const noPowerThresholdText = useFormatMessage('tax.noPowerThreshold', 'No power threshold requirement')
  const taxAppliesToFreeSessionsText = useFormatMessage('tax.taxAppliesToFreeSessions', 'Tax applies to free sessions')
  const noTaxOnFreeSessionsText = useFormatMessage('tax.noTaxOnFreeSessions', 'No tax on free sessions')
  const noMinimumTransactionAmountText = useFormatMessage('tax.noMinimumTransactionAmount', 'No minimum transaction amount')
  const configureTaxRuleText = useFormatMessage('tax.configureTaxRule', 'Configure the tax rule to see a summary of what will be applied')
  const previewText = useFormatMessage('tax.preview', 'Preview')
  const previewAlertText = useFormatMessage('tax.previewAlert', 'This configuration affects only the highlighted Line Item. Local Tax and Tax are calculated automatically and will not be affected by these settings.')
  const chargeAmountText = useFormatMessage('tax.chargeAmount', 'Charge Amount')
  const subTotalText = useFormatMessage('tax.subTotal', 'Sub-Total')
  const localTaxText = useFormatMessage('tax.localTax', 'Local Tax')
  const taxText = useFormatMessage('tax.tax', 'Tax')
  const totalText = useFormatMessage('tax.total', 'Total')
  const autoText = useFormatMessage('tax.auto', 'Auto')
  const policyApprovalText = useFormatMessage('tax.policyApproval', 'Policy Approval')
  const policyApprovalCheckboxText = useFormatMessage('tax.policyApprovalCheckbox', 'I agree that this tax rule has been approved by the SWTCH Policy team for it to be activated')
  const approverNameText = useFormatMessage('tax.approverName', 'Name')
  const enterApproverNameText = useFormatMessage('tax.enterApproverName', 'Enter your name')

  const getDecimalDigit = (value: number, position: number) =>
    Math.floor(Math.abs(value) * Math.pow(10, position)) % 10

  const formatDecimalValue = (value: number, maxDecimals: number = 4) => {
    let decimals = 2
    for (let p = maxDecimals; p >= 2; p--) {
      if (getDecimalDigit(value, p) !== 0) {
        decimals = p
        break
      }
    }
    return `${value.toFixed(decimals)}`
  }

  const formatPerKwhDisplay = (value?: number) => {
    if (value === undefined || value === null) {
      return 'Not set'
    }
    return `${formatDecimalValue(value, 6)} $/kWh`
  }

  const formatRateValue = (rateType: 'perKwh' | 'percentage', perKwh?: number, percentage?: number) => {
    if (rateType === 'perKwh') {
      return formatPerKwhDisplay(perKwh)
    }
    if (percentage === undefined || percentage === null) {
      return 'Not set'
    }
    return `${formatDecimalValue(percentage)}%`
  }

  const getRateLabelForRecord = (record: TaxRule) =>
    record.rateType === 'perKwh' ? ratePerKwhLabelText : ratePercentageLabelText

  const getLocationLabelForRecord = (record: TaxRule) =>
    record.locationType === 'states' ? statesText : zipCodesText

  const getLocationDetailsForRecord = (record: TaxRule) => {
    if (!record.locationDetails || record.locationDetails.length === 0) {
      return notSpecifiedText
    }
    if (record.locationType === 'states') {
      return record.locationDetails.map((code) => STATE_LABEL_MAP[code] ?? code).join(', ')
    }
    return record.locationDetails.join(', ')
  }

  const getPowerRangeForRecord = (record: TaxRule) => {
    if (record.powerThresholdMin === null && record.powerThresholdMax === null) {
      return noPowerThresholdText
    }
    const min = record.powerThresholdMin !== null && record.powerThresholdMin !== undefined ? formatDecimalValue(record.powerThresholdMin) : null
    const max = record.powerThresholdMax !== null && record.powerThresholdMax !== undefined ? formatDecimalValue(record.powerThresholdMax) : null
    
    if (min !== null && max !== null) {
      return `${min} - ${max} kW`
    } else if (min !== null && max === null) {
      return `≥ ${min} kW`
    } else if (min === null && max !== null) {
      return `≤ ${max} kW`
    }
    return noPowerThresholdText
  }

  const getMinimumTransactionAmountForRecord = (record: TaxRule) => {
    if (record.minimumTransactionAmount === null || record.minimumTransactionAmount === undefined || record.minimumTransactionAmount === 0) {
      return noMinimumTransactionAmountText
    }
    return `$${record.minimumTransactionAmount.toFixed(2)}`
  }

  const getFreeSessionExemptForRecord = (record: TaxRule) => {
    return record.isFreeSessionExempt ? noTaxOnFreeSessionsText : taxAppliesToFreeSessionsText
  }

  const handleEdit = async (record: TaxRule) => {
    try {
      setLoadingRecord(true)
      setSubmitError(undefined)
      
      // Check if we have saved state for this record
      const hasSavedStateForThisRecord = savedFormState && savedFormState.editingId === record.id
      
      setEditingId(record.id)
      
      // Clear saved state if editing a different record
      if (savedFormState && savedFormState.editingId !== record.id) {
        setSavedFormState(null)
      }
      
      // If we have saved state for this record, restore it instead of fetching from API
      if (hasSavedStateForThisRecord) {
        const { formValues, locationSpec, powerThreshold, minTransaction, rateType, applyTaxToFree, taxName, taxDisplayName, displayNotes } = savedFormState
        
        // Restore form values
        form.setFieldsValue(formValues)
        
        // Restore state values
        setLocationSpecification(locationSpec)
        setPowerThresholdEnabled(powerThreshold)
        setMinimumTransactionAmountEnabled(minTransaction)
        setRateType(rateType)
        setApplyTaxToFreeSessions(applyTaxToFree)
        setTaxNameValue(taxName)
        setTaxDisplayNameValue(taxDisplayName)
        setDisplayNotesValue(displayNotes)
        
        // Open modal
        setIsModalOpen(true)
        setLoadingRecord(false)
        return
      }
      
      // Otherwise, fetch from API
      
      // Fetch the full record from API
      const taxConfig = await GetTaxConfigurationById(parseInt(record.id))
      
      // Map backend data to form values
      const formValues: any = {
        taxName: taxConfig.taxName,
        taxType: taxConfig.taxType === 'excise_tax' ? 'excise' : taxConfig.taxType,
        taxDescription: taxConfig.taxDescription || '',
        country: taxConfig.country === 'US' ? 'united-states' : taxConfig.country,
        locationSpecification: taxConfig.states && taxConfig.states.length > 0 ? 'byState' : 'byZipCodes',
        taxDisplayName: taxConfig.taxNameDisplay || '',
        displayNotes: taxConfig.taxNotesDisplay || '',
        taxAuthorityContact: taxConfig.taxAuthorityContact || '',
        regulatoryReference: taxConfig.regulatoryReference || '',
      }
      
      // Set location data
      if (taxConfig.states && taxConfig.states.length > 0) {
        formValues.states = taxConfig.states
        formValues.zipCodes = undefined
        setLocationSpecification('byState')
      } else if (taxConfig.zipCodes && taxConfig.zipCodes.length > 0) {
        formValues.zipCodes = taxConfig.zipCodes.join(', ')
        formValues.states = undefined
        setLocationSpecification('byZipCodes')
      }
      
      // Set power threshold
      // Check if power threshold is enabled: both null OR (min is 0 and max is null) means disabled
      const minValue = taxConfig.powerThresholdMin !== null && taxConfig.powerThresholdMin !== undefined 
        ? parseFloat(taxConfig.powerThresholdMin) 
        : null
      const maxValue = taxConfig.powerThresholdMax !== null && taxConfig.powerThresholdMax !== undefined 
        ? parseFloat(taxConfig.powerThresholdMax) 
        : null
      
      const isPowerThresholdDisabled = 
        (minValue === null && maxValue === null) || 
        (minValue === 0 && maxValue === null)
      
      if (!isPowerThresholdDisabled) {
        setPowerThresholdEnabled(true)
        formValues.powerThresholdFrom = minValue !== null ? minValue : undefined
        formValues.powerThresholdTo = maxValue !== null ? maxValue : undefined
      } else {
        setPowerThresholdEnabled(false)
        formValues.powerThresholdFrom = undefined
        formValues.powerThresholdTo = undefined
      }
      
      // Set rate type and values
      const taxRatePercent = parseFloat(taxConfig.taxRatePercent || '0')
      const taxRateKwh = parseFloat(taxConfig.taxRateKwh || '0')
      
      if (taxRatePercent === 0 && taxRateKwh !== 0) {
        // Per kWh rate
        setRateType('perKwh')
        formValues.rateType = 'perKwh'
        formValues.rateValuePerKwh = taxRateKwh
        formValues.rateValuePercentage = undefined
      } else if (taxRatePercent !== 0) {
        // Percentage rate
        setRateType('percentage')
        formValues.rateType = 'percentage'
        formValues.rateValuePercentage = taxRatePercent
        formValues.rateValuePerKwh = undefined
        // Note: taxAppliesTo might need to come from backend, defaulting to energyCostOnly
        formValues.taxAppliesTo = 'energyCostOnly'
      } else {
        // Default to perKwh
        setRateType('perKwh')
        formValues.rateType = 'perKwh'
      }
      
      // Set free session exemption (inverted)
      setApplyTaxToFreeSessions(!taxConfig.isFreeSessionExempt)
      formValues.applyTaxToFreeSessions = !taxConfig.isFreeSessionExempt
      
      // Set minimum transaction amount
      if (taxConfig.minimumTransactionAmount !== null && taxConfig.minimumTransactionAmount !== undefined) {
        const minAmount = parseFloat(taxConfig.minimumTransactionAmount)
        if (minAmount > 0) {
          setMinimumTransactionAmountEnabled(true)
          formValues.minimumTransactionAmount = minAmount
        } else {
          setMinimumTransactionAmountEnabled(false)
          formValues.minimumTransactionAmount = undefined
        }
      } else {
        setMinimumTransactionAmountEnabled(false)
        formValues.minimumTransactionAmount = undefined
      }
      
      // Set approved by name
      formValues.approvedByName = taxConfig.approvedByName || ''
      
      // Set form values
      form.setFieldsValue(formValues)
      
      // Sync state values for character counters
      setTaxNameValue(formValues.taxName || '')
      setTaxDisplayNameValue(formValues.taxDisplayName || '')
      setDisplayNotesValue(formValues.displayNotes || '')
      
      // Open modal
      setIsModalOpen(true)
    } catch (err) {
      console.error('Error fetching tax configuration:', err)
      setSubmitError(err)
    } finally {
      setLoadingRecord(false)
    }
  }

  const handleView = async (record: TaxRule) => {
    try {
      setLoadingRecord(true)
      setRecordToView(record)
      
      // Fetch the full record from API
      const taxConfig = await GetTaxConfigurationById(parseInt(record.id))
      setRecordToView({
        ...record,
        fullDetails: taxConfig,
      } as any)
      
      setIsViewModalOpen(true)
    } catch (err) {
      console.error('Error fetching tax configuration:', err)
      openErrorNotification('Failed to load tax rule details')
    } finally {
      setLoadingRecord(false)
    }
  }

  const handleArchiveWithConfirmation = (record: TaxRule) => {
    setRecordToArchive(record)
    setIsArchiveConfirmModalOpen(true)
  }

  const handleConfirmArchive = async () => {
    if (!recordToArchive) return
    
    setIsArchiveConfirmModalOpen(false)
    await handleArchive(recordToArchive)
    setRecordToArchive(null)
  }

  const handleArchive = async (record: TaxRule) => {
    try {
      setSubmitting(true)
      setSubmitError(undefined)
      
      // PATCH to update only status to archive (will be mapped to 'inactive' for backend)
      const payload: any = {
        status: 'archive',
      }
      
      await UpdateTaxConfiguration(parseInt(record.id), payload)
      
      // Show success notification
      openSuccessNotification(`Tax configuration "${record.name}" has been successfully archived.`)
      
      // Refresh the table
      fetchTaxConfigurations(pagination.currentPage, pagination.perPage)
    } catch (err: any) {
      console.error('Error archiving tax configuration:', err)
      console.error('Error details:', JSON.stringify(err, null, 2))
      
      // Extract error message from API response
      // The API returns { "errors": [...] } which gets converted to SwtchError with description = joined errors
      let errorMessage = 'An error occurred while archiving the tax configuration.'
      
      // Helper function to extract error message recursively
      const extractErrorMessage = (error: any, depth: number = 0): string | null => {
        if (!error || depth > 5) return null // Prevent infinite recursion
        
        // FIRST: Recursively check cause (this is critical - the actual API error is often wrapped in cause)
        // When SwtchClientError wraps the error, the real error is in cause
        if (error.cause) {
          const causeMessage = extractErrorMessage(error.cause, depth + 1)
          if (causeMessage) return causeMessage
        }
        
        // Check if it's SwtchApiError
        if (error instanceof SwtchApiError) {
          // Check for errors array (from API response { "errors": [...] })
          if ((error as any).errors && Array.isArray((error as any).errors) && (error as any).errors.length > 0) {
            return (error as any).errors.join(' ')
          }
          // Check details array
          if (error.details && Array.isArray(error.details) && error.details.length > 0) {
            return error.details.join(' ')
          }
          // Check message (SwtchApiError.message contains joined errors when data.errors exists)
          if (error.message && error.message.trim()) {
            return error.message
          }
        }
        
        // Check for errors array directly (might be preserved from JSON)
        if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
          return error.errors.join(' ')
        }
        
        // Check description (SwtchError stores message here - this is where the joined errors end up)
        // When responseToSwtchError creates SwtchError, it uses result.message which contains joined errors
        if (error.description && 
            error.description !== 'Unable to perform HTTP request correctly' && 
            error.description !== 'Error Occurred' &&
            error.description.trim()) {
          return error.description
        }
        
        // Check messages array (SwtchError.messages)
        if (error.messages && Array.isArray(error.messages) && error.messages.length > 0) {
          return error.messages.join(' ')
        }
        
        // Check details array
        if (error.details && Array.isArray(error.details) && error.details.length > 0) {
          return error.details.join(' ')
        }
        
        // Check message (but skip generic errors)
        if (error.message && 
            error.message !== 'Unable to perform HTTP request correctly' && 
            error.message !== 'Error Occurred' &&
            error.message.trim()) {
          return error.message
        }
        
        return null
      }
      
      const extractedMessage = extractErrorMessage(err)
      if (extractedMessage) {
        errorMessage = extractedMessage
      }
      
      openErrorNotification(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }


  const handleOpenModal = () => {
    setIsModalOpen(true)
    setSubmitError(undefined)
    
    // Restore saved form state if it exists and matches current context
    if (savedFormState && savedFormState.editingId === null) {
      // Restore for new rule creation
      const { formValues, locationSpec, powerThreshold, minTransaction, rateType, applyTaxToFree, taxName, taxDisplayName, displayNotes } = savedFormState
      
      setEditingId(null)
      // Restore form values
      form.setFieldsValue(formValues)
      
      // Restore state values
      setLocationSpecification(locationSpec)
      setPowerThresholdEnabled(powerThreshold)
      setMinimumTransactionAmountEnabled(minTransaction)
      setRateType(rateType)
      setApplyTaxToFreeSessions(applyTaxToFree)
      setTaxNameValue(taxName)
      setTaxDisplayNameValue(taxDisplayName)
      setDisplayNotesValue(displayNotes)
    } else {
      // Reset for new rule or when saved state doesn't match
      setEditingId(null)
      setTaxNameValue('')
      setTaxDisplayNameValue('')
      setDisplayNotesValue('')
      setLocationSpecification('byState')
      setPowerThresholdEnabled(false)
      setMinimumTransactionAmountEnabled(false)
      setRateType('perKwh')
      setApplyTaxToFreeSessions(false)
      form.resetFields()
    }
  }

  const handleCloseModal = () => {
    // Save form state before closing (when clicking outside)
    const formValues = form.getFieldsValue()
    setSavedFormState({
      editingId: editingId, // Save which record was being edited (null for new)
      formValues,
      locationSpec: locationSpecification,
      powerThreshold: powerThresholdEnabled,
      minTransaction: minimumTransactionAmountEnabled,
      rateType,
      applyTaxToFree: applyTaxToFreeSessions,
      taxName: taxNameValue,
      taxDisplayName: taxDisplayNameValue,
      displayNotes: displayNotesValue,
    })
    
    setIsModalOpen(false)
    setSubmitError(undefined)
    // Don't reset form or state here - keep them for next open
  }

  const handleCancelModal = () => {
    // Explicitly cancel - reset everything and clear saved state
    setIsModalOpen(false)
    form.resetFields()
    setLocationSpecification('byState')
    setPowerThresholdEnabled(false)
    setMinimumTransactionAmountEnabled(false)
    setRateType('perKwh')
    setApplyTaxToFreeSessions(false)
    setTaxNameValue('')
    setTaxDisplayNameValue('')
    setDisplayNotesValue('')
    setSubmitError(undefined)
    setEditingId(null)
    setSavedFormState(null) // Clear saved state
  }

  const handleSubmitWithConfirmation = async () => {
    // Clear any previous errors
    setSubmitError(undefined)
    
    try {
      // Validate all fields first - this will throw if validation fails
      await form.validateFields()
      
      // If validation passes, show confirmation modal
      setIsConfirmModalOpen(true)
    } catch (err: any) {
      // Validation failed, handle error
      console.error('Validation error:', err)
      if (err.errorFields && err.errorFields.length > 0) {
        const firstError = err.errorFields[0]
        setSubmitError(new Error(firstError.errors?.[0] || 'Please fix the form errors before submitting.'))
      } else {
        setSubmitError(err)
      }
    }
  }

  const handleConfirmAndSubmit = async () => {
    // Close confirmation modal
    setIsConfirmModalOpen(false)
    
    // Proceed with actual submission
    await handleSubmit('active')
  }

  const handleSubmit = async (status: 'draft' | 'active') => {
    // Clear any previous errors
    setSubmitError(undefined)
    
    try {
      // Validate all fields first - this will throw if validation fails
      const values = await form.validateFields()
      
      // Only set submitting to true after validation passes
      setSubmitting(true)

      // Map form values to API payload (camelCase)
      const payload: any = {
        taxName: values.taxName,
        taxType: values.taxType === 'excise' ? 'excise_tax' : values.taxType,
        taxDescription: values.taxDescription || '',
        country: values.country === 'united-states' ? 'US' : values.country,
        status: status,
        taxNameDisplay: values.taxDisplayName || null,
        taxNotesDisplay: values.displayNotes || null,
        taxAuthorityContact: values.taxAuthorityContact || null,
        regulatoryReference: values.regulatoryReference || null,
      }

      // Handle location (states or zipCodes)
      if (values.locationSpecification === 'byState' && values.states && values.states.length > 0) {
        payload.states = values.states
        payload.zipCodes = []
      } else if (values.locationSpecification === 'byZipCodes' && values.zipCodes) {
        // Parse comma-separated ZIP codes
        const zipCodes = values.zipCodes
          .split(',')
          .map((zip: string) => zip.trim())
          .filter((zip: string) => zip !== '')
        payload.zipCodes = zipCodes
        payload.states = []
      }

      // Handle power threshold
      if (powerThresholdEnabled) {
        payload.powerThresholdMin = values.powerThresholdFrom !== undefined && values.powerThresholdFrom !== null
          ? values.powerThresholdFrom.toString()
          : '0.0'
        payload.powerThresholdMax = values.powerThresholdTo !== undefined && values.powerThresholdTo !== null
          ? values.powerThresholdTo.toString()
          : null
      } else {
        payload.powerThresholdMin = '0.0'
        payload.powerThresholdMax = null
      }

      // Handle rate type and values
      if (values.rateType === 'perKwh') {
        // Per kWh rate
        payload.taxRateKwh = values.rateValuePerKwh !== undefined && values.rateValuePerKwh !== null
          ? values.rateValuePerKwh.toString()
          : '0.0'
        payload.taxRatePercent = '0.0'
        payload.isTaxPercentOnKwh = false
      } else {
        // Percentage rate
        payload.taxRatePercent = values.rateValuePercentage !== undefined && values.rateValuePercentage !== null
          ? values.rateValuePercentage.toString()
          : '0.0'
        payload.taxRateKwh = '0.0'
        payload.isTaxPercentOnKwh = true
      }

      // Handle free session exemption (inverted: applyTaxToFreeSessions = !isFreeSessionExempt)
      payload.isFreeSessionExempt = !applyTaxToFreeSessions

      // Handle minimum transaction amount
      if (minimumTransactionAmountEnabled) {
        payload.minimumTransactionAmount = values.minimumTransactionAmount !== undefined && values.minimumTransactionAmount !== null
          ? values.minimumTransactionAmount.toString()
          : null
      } else {
        payload.minimumTransactionAmount = null
      }

      // Handle approved by name (only if provided)
      if (values.approvedByName) {
        payload.approvedByName = values.approvedByName
      }

      // Use PATCH for edit, POST for create
      if (editingId) {
        await UpdateTaxConfiguration(parseInt(editingId), payload)
      } else {
        await CreateTaxConfiguration(payload)
      }
      
      // Only close modal and refresh on success (2xx response)
      // Refresh the table
      fetchTaxConfigurations(pagination.currentPage, pagination.perPage)
      
      // Close modal, reset form, and clear saved state
      setIsModalOpen(false)
      form.resetFields()
      setLocationSpecification('byState')
      setPowerThresholdEnabled(false)
      setMinimumTransactionAmountEnabled(false)
      setRateType('perKwh')
      setApplyTaxToFreeSessions(false)
      setTaxNameValue('')
      setTaxDisplayNameValue('')
      setDisplayNotesValue('')
      setSubmitError(undefined)
      setEditingId(null)
      setSavedFormState(null) // Clear saved state on successful submit
    } catch (err: any) {
      console.error('Error creating tax configuration:', err)
      
      // Handle form validation errors
      if (err.errorFields && Array.isArray(err.errorFields)) {
        // This is a form validation error from Ant Design
        const missingFields = err.errorFields.map((field: any) => {
          const fieldName = field.name?.[0] || field.name || 'unknown'
          // Map field names to user-friendly labels
          const fieldLabels: Record<string, string> = {
            taxName: 'Tax Name',
            taxType: 'Tax Type',
            country: 'Country',
            locationSpecification: 'Location Specification',
            states: 'States',
            zipCodes: 'ZIP Codes',
            taxDisplayName: 'Tax Display Name',
            displayNotes: 'Display Notes',
            rateType: 'Rate Type',
            rateValuePerKwh: 'Rate Value (per kWh)',
            rateValuePercentage: 'Rate Value (Percentage)',
            taxAppliesTo: 'Tax Applies To',
            policyApprovalAgreement: 'Policy Approval Agreement',
            approvedByName: 'Approver Name',
          }
          
          const label = fieldLabels[fieldName] || fieldName
          const errorMessage = field.errors?.[0] || 'This field is required'
          return `${label}: ${errorMessage}`
        })
        
        // Create a SwtchError-like object for display
        setSubmitError({
          description: 'Validation Error',
          messages: missingFields,
          cause: undefined,
        } as any)
      } else if (err.messages || err.description) {
        // This is already a SwtchError
        setSubmitError(err)
      } else {
        // Generic error - format it
        const errorMessage = err.message || err.toString() || 'An error occurred while saving the tax configuration'
        setSubmitError({
          description: errorMessage,
          messages: [errorMessage],
          cause: err,
        } as any)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const renderTitle = () => {
    const [firstLine, secondLine] = taxComplianceDescriptionText.split('\n')
    return (
      <div>
        <p style={{ color: theme.colors.grey6 || theme.colors.gray || '#666', marginBottom: '10px', lineHeight: '1.6' }}>
          {firstLine}
          {secondLine && (
            <>
              <br />
              {secondLine}
            </>
          )}
        </p>
      </div>
    )
  }

  // Helper function to render truncated text with tooltip
  const renderTruncatedText = (text: string, maxLines: number = 2) => {
    if (!text) return <span>-</span>
    const content = (
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical',
          wordBreak: 'break-word',
          lineHeight: '1.5',
          maxHeight: `${maxLines * 1.5}em`,
        }}
      >
        {text}
      </div>
    )
    // Always show tooltip for consistency, but it will only appear on hover
    return <Tooltip title={text}>{content}</Tooltip>
  }

  const columns = [
    {
      title: taxNameText,
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string) => renderTruncatedText(text, 1),
    },
    {
      title: (
        <span>
          {taxDescriptionText}{' '}
          <Tooltip title="Optional notes about the excise tax.">
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0px', verticalAlign: 'middle' }}>
              <QuestionIcon color={theme.colors.lightGray} width={14} height={14} />
            </span>
          </Tooltip>
        </span>
      ),
      dataIndex: 'description',
      key: 'description',
      width: 250,
      render: (text: string) => renderTruncatedText(text, 2),
    },
    {
      title: (
        <span>
          {locationText}{' '}
          <Tooltip title="Where this excise tax applies — across States or ZIP codes.">
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0px', verticalAlign: 'middle' }}>
              <QuestionIcon color={theme.colors.lightGray} width={14} height={14} />
            </span>
          </Tooltip>
        </span>
      ),
      key: 'locationSummary',
      width: 220,
      render: (_: any, record: TaxRule) => {
        const locationDetails = getLocationDetailsForRecord(record)
        return (
          <div style={{ minHeight: '48px', display: 'flex', alignItems: 'center' }}>
            <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>
              {renderTruncatedText(locationDetails, 2)}
            </div>
          </div>
        )
      },
    },
    {
      title: (
        <span>
          {rateSummaryColumnText}{' '}
          <Tooltip title="How the excise tax is calculated (per kWh or %), and what charges it applies to.">
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0px', verticalAlign: 'middle' }}>
              <QuestionIcon color={theme.colors.lightGray} width={14} height={14} />
            </span>
          </Tooltip>
        </span>
      ),
      key: 'rateSummary',
      width: 280,
      render: (_: any, record: TaxRule) => {
        const rateLabel = getRateLabelForRecord(record)
        const rateValue = formatRateValue(record.rateType, record.rateCents, record.ratePercentage)
        const appliesToText = record.rateType === 'percentage' 
          ? `${taxAppliesToText}: ${record.appliesTo === 'allCost' ? allCostText : energyCostOnlyText}`
          : ''
        return (
          <div style={{ minHeight: '48px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ color: theme.colors.softBlack, fontWeight: 600, marginBottom: '4px' }}>
              {renderTruncatedText(rateLabel, 1)}
            </div>
            <div style={{ color: theme.colors.grey7, fontSize: '14px', margin: '4px 0' }}>
              {renderTruncatedText(rateValue, 1)}
            </div>
            {record.rateType === 'percentage' && (
              <div style={{ color: theme.colors.grey6, fontSize: '12px', marginTop: '2px' }}>
                {renderTruncatedText(appliesToText, 1)}
              </div>
            )}
          </div>
        )
      },
    },
    {
      title: (
        <span>
          {powerRangeText}{' '}
          <Tooltip title="Charger power levels where this excise tax applies.">
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0px', verticalAlign: 'middle' }}>
              <QuestionIcon color={theme.colors.lightGray} width={14} height={14} />
            </span>
          </Tooltip>
        </span>
      ),
      key: 'powerRange',
      width: 180,
      render: (_: any, record: TaxRule) => {
        const text = getPowerRangeForRecord(record)
        return (
          <div style={{ minHeight: '48px', display: 'flex', alignItems: 'center' }}>
            <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>
              {renderTruncatedText(text, 1)}
            </div>
          </div>
        )
      },
    },
    {
      title: (
        <span>
          {minimumTransactionAmountText}{' '}
          <Tooltip title="The minimum session charge required before this excise tax is applied.">
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0px', verticalAlign: 'middle' }}>
              <QuestionIcon color={theme.colors.lightGray} width={14} height={14} />
            </span>
          </Tooltip>
        </span>
      ),
      key: 'minimumTransactionAmount',
      width: 200,
      render: (_: any, record: TaxRule) => {
        const text = getMinimumTransactionAmountForRecord(record)
        return (
          <div style={{ minHeight: '48px', display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              color: theme.colors.grey7, 
              fontSize: '14px',
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              lineHeight: '1.5'
            }}>
              {text}
            </div>
          </div>
        )
      },
    },
    {
      title: (
        <span style={{ whiteSpace: 'pre-line' }}>
          {applyTaxToFreeSessionsText}{' '}
          <Tooltip title="If enabled, sessions that cost $0 won't be charged the tax rule.">
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0px', verticalAlign: 'middle' }}>
              <QuestionIcon color={theme.colors.lightGray} width={14} height={14} />
            </span>
          </Tooltip>
        </span>
      ),
      key: 'freeSessionExempt',
      width: 200,
      render: (_: any, record: TaxRule) => {
        const text = getFreeSessionExemptForRecord(record)
        return (
          <div style={{ minHeight: '48px', display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              color: theme.colors.grey7, 
              fontSize: '14px',
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              lineHeight: '1.5'
            }}>
              {text}
            </div>
          </div>
        )
      },
    },
    {
      title: (
        <span>
          {statusText}{' '}
          <Tooltip title="Active rules applies the excise tax to new sessions. Archived rules do not apply and cannot be re-activated. Draft rules are not live yet.">
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0px', verticalAlign: 'middle' }}>
              <QuestionIcon color={theme.colors.lightGray} width={14} height={14} />
            </span>
          </Tooltip>
        </span>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => {
        const isActive = status === 'active'
        const isDraft = status === 'draft'
        const badgeStyles = isActive
          ? { color: theme.colors.white, backgroundColor: theme.colors.neonGreen }
          : isDraft
            ? { color: theme.colors.warningPrimary, backgroundColor: theme.colors.warningSecondary }
            : { color: theme.colors.grey6, backgroundColor: theme.colors.grey2 }

        const label = isActive ? activeText : isDraft ? draftText : archiveText

        return (
          <div style={{ minHeight: '48px', display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                ...badgeStyles,
                padding: '4px 12px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: 500,
                display: 'inline-block',
              }}
            >
              {label}
            </span>
          </div>
        )
      },
    },
    {
      title: (
        <span>
          {lastUpdatedText}{' '}
          <Tooltip title="The most recent date and time this excise tax rule was changed.">
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0px', verticalAlign: 'middle' }}>
              <QuestionIcon color={theme.colors.lightGray} width={14} height={14} />
            </span>
          </Tooltip>
        </span>
      ),
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      width: 180,
      render: (lastUpdated: string) => {
        if (!lastUpdated) {
          return (
            <div style={{ minHeight: '48px', display: 'flex', alignItems: 'center' }}>
              <span>-</span>
            </div>
          )
        }
        const date = new Date(lastUpdated)
        const dateStr = date.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
        const timeStr = date.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        })
        return (
          <div style={{ minHeight: '48px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>
              {dateStr}
            </div>
            <div style={{ color: theme.colors.grey6, fontSize: '12px', marginTop: '2px' }}>
              {timeStr}
            </div>
          </div>
        )
      },
    },
    {
      title: (
        <span>
          {actionsText}{' '}
          <Tooltip title="View, edit, or archive this excise tax rule.">
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0px', verticalAlign: 'middle' }}>
              <QuestionIcon color={theme.colors.lightGray} width={14} height={14} />
            </span>
          </Tooltip>
        </span>
      ),
      key: 'actions',
      width: 200,
      render: (_: any, record: TaxRule) => {
        const iconStyle = { fontSize: '20px' }
        const baseButtonStyle = { 
          padding: 0, 
          border: 'none',
          color: theme.colors.grey7 || '#262626',
        }
        const disabledButtonStyle = {
          ...baseButtonStyle,
          color: theme.colors.grey5 || '#d9d9d9',
          cursor: 'not-allowed',
          opacity: 0.5,
        }

        const renderActionButton = (
          icon: React.ReactNode,
          enabled: boolean,
          onClick: (() => void) | undefined,
          title: string,
        ) => (
          <Tooltip title={title}>
            <span style={{ display: 'inline-block' }}>
              <ActionIconButton
                type="link"
                icon={icon}
                size="large"
                disabled={!enabled}
                onClick={enabled ? onClick : undefined}
                style={enabled ? baseButtonStyle : disabledButtonStyle}
                className="tax-action-icon-button"
              />
            </span>
          </Tooltip>
        )

        const editEnabled = record.status === 'draft'
        const archiveEnabled = record.status === 'active'

        return (
          <Space>
            {renderActionButton(
              <EyeOutlined style={iconStyle} />,
              true,
              () => handleView(record),
              'View tax rule',
            )}
            {renderActionButton(
              <EditOutlined style={iconStyle} />,
              editEnabled,
              () => handleEdit(record),
              editEnabled ? 'Edit draft' : 'Edit (disabled)',
            )}
            {renderActionButton(
              <InboxOutlined style={iconStyle} />,
              archiveEnabled,
              () => handleArchiveWithConfirmation(record),
              archiveEnabled ? 'Archive' : 'Archive (disabled)',
            )}
          </Space>
        )
      },
    },
  ]

  const { TabPane } = Tabs

  return (
    <BackgroundContainer>
      <style>{`
        html, body {
          line-height: 12px !important;
        }

        .ant-select, .ant-tabs {
          line-height: 12px !important;
        }

        /* Make checkbox non-interactive so clicking text selects the option */
        .ant-select-dropdown .ant-select-item-option .ant-checkbox-wrapper {
          pointer-events: none;
        }
        
        .ant-select-dropdown .ant-select-item-option .ant-checkbox {
          pointer-events: none;
        }
        
        .ant-select-dropdown .ant-select-item-option .ant-checkbox-inner {
          pointer-events: none;
        }
        
        /* Remove checkmark icon from selected tags, but keep the text visible */
        /* Hide the checkbox input and inner checkbox, but keep the wrapper and text */
        .ant-select-selection-item .ant-checkbox,
        .ant-select-selection-item .ant-checkbox-input,
        .ant-select-selection-item .ant-checkbox-inner {
          display: none !important;
          visibility: hidden !important;
          width: 0 !important;
          height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          opacity: 0 !important;
        }
        
        /* Keep the checkbox wrapper visible but remove spacing for the hidden checkbox */
        .ant-select-selection-item .ant-checkbox-wrapper {
          display: inline-block !important;
          visibility: visible !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        /* Ensure text content is visible and properly displayed */
        .ant-select-selection-item {
          display: inline-flex !important;
          align-items: center !important;
          padding: 8px 8px !important;
          height: auto !important;
          min-height: 24px !important;
        }
        
        .ant-select-selection-item .ant-select-selection-item-content {
          display: inline-flex !important;
          align-items: center !important;
          visibility: visible !important;
          opacity: 1 !important;
          width: auto !important;
          height: auto !important;
          margin: 0 !important;
          padding: 0 !important;
          line-height: 1.2 !important;
        }
        
        /* Make sure the text span inside checkbox wrapper is visible */
        .ant-select-selection-item .ant-checkbox-wrapper {
          display: inline-flex !important;
          align-items: center !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        .ant-select-selection-item .ant-checkbox-wrapper > span:not(.ant-checkbox):not(.ant-checkbox-inner) {
          display: inline !important;
          visibility: visible !important;
          margin-left: 0 !important;
          line-height: 1.2 !important;
        }
        
        .ant-tabs {
          margin-bottom: 0 !important;
          margin-top: 12px !important;
        }
        .ant-tabs-nav {
          margin-bottom: 0 !important;
          padding-top: 0 !important;
        }
        .ant-tabs-content-holder {
          padding-top: 16px !important;
        }
        .ant-tabs-tabpane {
          padding-top: 0 !important;
        }
        .ant-tabs-tab-btn {
          font-size: 12px !important;
          line-height: 14px !important;
          padding: 7.5px 20px !important;
          font-weight: 700 !important;
          height: auto !important;
        }
        .ant-tabs-nav-list {
          border-radius: 41px !important;
          background-color: ${theme.colors.grayBackground || '#fafafa'} !important;
          border: 1px solid ${theme.colors.lightGray} !important;
          padding: 3px !important;
        }
        .ant-tabs-tab {
          margin: 0 !important;
          padding: 0 !important;
        }
        .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #fff !important;
          background-color: ${theme.colors.softBlack || '#262626'} !important;
        }
        .ant-tabs-tab:not(.ant-tabs-tab-active) .ant-tabs-tab-btn {
          color: ${theme.colors.softBlack || '#262626'} !important;
          background-color: #fafafa !important;
        }
        .ant-page-header {
          padding-top: 0 !important;
          padding-bottom: 16px !important;
        }
        .ant-page-header-heading-title {
          font-weight: normal !important;
        }
        .ant-page-header-heading-title p {
          margin-bottom: 10px !important;
          font-weight: normal !important;
          font-size: 1.2rem !important;
        }
      `}</style>
      <Tabs defaultActiveKey="excise-tax" style={{ marginBottom: '0' }}>
        <TabPane tab={<span className="regular-cap">{exciseTaxText.toUpperCase()}</span>} key="excise-tax">
          <PageHeader
            title={renderTitle()}
            style={{ paddingTop: '0', marginTop: '0' }}
          />
          <AlertError error={error} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '16px' }}>
            <SearchBarContainer>
        <Select
          className="rounded-select"
          mode="multiple"
          placeholder={filterTaxNameText}
          value={filterTaxName}
          onChange={(value: string[]) => {
            setFilterTaxName(value)
            handleFilterChange({
              ...filters,
              taxName: value.length > 0 ? value : undefined,
            })
          }}
          allowClear
          showSearch
          onSearch={debouncedSearchTaxNames}
          filterOption={false}
          notFoundContent={fetchingTaxNames ? <Spin size="small" /> : null}
          style={{ flex: '1 1 0', minWidth: 0 }}
          maxTagCount={filterTaxName.length === 1 ? 1 : 0}
          maxTagPlaceholder={(omittedValues) => `${omittedValues.length} selected`}
        >
          {taxNameOptions.map((option) => (
            <Option key={option.value} value={option.value} label={option.label}>
              <Checkbox checked={filterTaxName.includes(option.value)}>
                {option.label}
              </Checkbox>
            </Option>
          ))}
        </Select>
        <Select
          placeholder={filterLocationTypeText}
          value={filterLocationType}
          onChange={(value) => {
            setFilterLocationType(value)
            setFilterLocation([])
            handleFilterChange({
              ...filters,
              locationType: value,
              location: undefined,
            })
          }}
          allowClear
          style={{ flex: '1 1 0', minWidth: 0 }}
        >
          <Option value="state">{stateTextFilter}</Option>
          <Option value="zip">{zipCodeTextFilter}</Option>
        </Select>
        {filterLocationType === 'state' ? (
          <Select
            mode="multiple"
            placeholder={filterLocationText}
            value={filterLocation}
            onChange={(value: string[]) => {
              setFilterLocation(value)
              handleFilterChange({
                ...filters,
                locationType: 'state',
                location: value.length > 0 ? value : undefined,
              })
            }}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label as unknown as string)?.toLowerCase().includes(input.toLowerCase())
            }
            style={{ flex: '1 1 0', minWidth: 0, lineHeight: '32px' }}
            maxTagCount={filterLocation.length === 1 ? 1 : 0}
            maxTagPlaceholder={(omittedValues) => `${omittedValues.length} selected`}
          >
            {STATE_OPTIONS.map((state) => (
              <Option key={state.value} value={state.value} label={state.label}>
                <Checkbox checked={filterLocation.includes(state.value)}>
                  {state.label}
                </Checkbox>
              </Option>
            ))}
          </Select>
        ) : filterLocationType === 'zip' ? (
          <Select
            mode="tags"
            placeholder={filterLocationText}
            value={filterLocation}
            onChange={(value: string[]) => {
              setFilterLocation(value)
              handleFilterChange({
                ...filters,
                locationType: 'zip',
                location: value.length > 0 ? value : undefined,
              })
            }}
            allowClear
            showSearch
            onSearch={debouncedSearchZipCodes}
            filterOption={false}
            notFoundContent={fetchingZipCodes ? <Spin size="small" /> : null}
            style={{ flex: '1 1 0', minWidth: 0 }}
            maxTagCount={filterLocation.length === 1 ? 1 : 0}
            maxTagPlaceholder={(omittedValues) => `${omittedValues.length} selected`}
          >
            {zipCodeOptions.map((option) => (
              <Option key={option.value} value={option.value} label={option.label}>
                <Checkbox checked={filterLocation.includes(option.value)}>
                  {option.label}
                </Checkbox>
              </Option>
            ))}
          </Select>
        ) : (
          <Input placeholder={filterLocationText} disabled style={{ flex: '1 1 0', minWidth: 0 }} />
        )}
        <Select
          mode="multiple"
          placeholder={filterStatusText}
          value={filterStatus}
          onChange={(value: ('active' | 'archive' | 'draft')[]) => {
            setFilterStatus(value)
            // Map frontend "archive" to backend "inactive"
            const mapStatusToBackend = (status: string) => status === 'archive' ? 'inactive' : status
            const backendStatus = value.length > 0 
              ? (Array.isArray(value) ? value.map(mapStatusToBackend) : mapStatusToBackend(value))
              : undefined
            handleFilterChange({
              ...filters,
              status: backendStatus as any,
            })
          }}
          allowClear
          style={{ flex: '1 1 0', minWidth: 0 }}
          maxTagCount={filterStatus.length === 1 ? 1 : 0}
          maxTagPlaceholder={(omittedValues) => `${omittedValues.length} selected`}
        >
          <Option value="active" label={activeText}>
            <Checkbox checked={filterStatus.includes('active')}>
              {activeText}
            </Checkbox>
          </Option>
          <Option value="archive" label={archiveText}>
            <Checkbox checked={filterStatus.includes('archive')}>
              {archiveText}
            </Checkbox>
          </Option>
          <Option value="draft" label={draftText}>
            <Checkbox checked={filterStatus.includes('draft')}>
              {draftText}
            </Checkbox>
          </Option>
        </Select>
        <Select
          className="rounded-select"
          placeholder={sortByText}
          value={sortByLastUpdated}
          onChange={handleSortChange}
          style={{ flex: '1 1 0', minWidth: 0 }}
        >
          <Option value="desc">{sortDescText}</Option>
          <Option value="asc">{sortAscText}</Option>
          <Option value="none">{sortNoneText}</Option>
        </Select>
        <ButtonIcon
          icon={magnifyBlackSVG}
          loading={loading}
          disabled={loading}
          fetchData={() => fetchTaxConfigurations(pagination.currentPage, pagination.perPage)}
          withinSearchBar
        />
      </SearchBarContainer>
      <Button
        type="primary"
        size="large"
        icon={plusSVG}
        onClick={handleOpenModal}
        disabled={loading}
        style={{
          backgroundColor: theme.colors.neonGreen,
          borderColor: theme.colors.neonGreen,
          height: '39px',
          flexShrink: 0,
        }}
      >
        {newTaxRuleText}
      </Button>
    </div>
      <Box>
        <style>{`
          .tax-table .ant-table-tbody > tr > td {
            vertical-align: middle;
            padding: 12px 16px;
          }
          .tax-table .ant-table-tbody > tr {
            height: 64px;
            min-height: 64px;
          }
          /* Remove padding from styled-components auto-generated classes */
          [class^="sc-"],
          [class*=" sc-"] {
            padding: 0 !important;
          }
          /* Make tooltips auto-wrap */
          .ant-tooltip-inner {
            white-space: normal !important;
            word-wrap: break-word !important;
            max-width: 300px !important;
          }
        `}</style>
        <TaxTableContainer>
          <Table
            className="tax-table"
            columns={columns}
            dataSource={taxRules}
          rowKey="id"
          loading={loading}
          pagination={{
            position: ['bottomCenter'],
            current: pagination.currentPage,
            pageSize: pagination.perPage,
            total: pagination.totalEntries,
            showSizeChanger: false,
            onChange: (page, pageSize) => handlePaginationChange(page, pageSize),
          }}
        />
        </TaxTableContainer>
      </Box>

      <Modal
        title={editingId ? 'Edit Tax Rule' : createTaxRuleText}
        visible={isModalOpen}
        onCancel={handleCloseModal}
        footer={
          <Row gutter={16}>
            <Col span={6}>
              <Button
                block
                onClick={handleCancelModal}
                style={{
                  backgroundColor: theme.colors.white,
                  color: theme.colors.grey7,
                  borderColor: theme.colors.grey2,
                }}
              >
                {cancelText}
              </Button>
            </Col>
            <Col span={9}>
              <Button
                block
                onClick={() => handleSubmit('draft')}
                style={{
                  backgroundColor: theme.colors.white,
                  color: theme.colors.grey7,
                  borderColor: theme.colors.grey2,
                }}
                loading={submitting || loadingRecord}
                disabled={submitting || loadingRecord}
              >
                {saveAsDraftText}
              </Button>
            </Col>
            <Col span={9}>
              <Button
                block
                type="primary"
                onClick={handleSubmitWithConfirmation}
                style={{
                  backgroundColor: theme.colors.neonGreen,
                  borderColor: theme.colors.neonGreen,
                }}
                loading={submitting || loadingRecord}
                disabled={submitting || loadingRecord}
              >
                {saveAndActivateText}
              </Button>
            </Col>
          </Row>
        }
        width="50%"
      >
        <style>{`
          .ant-modal-body .ant-input,
          .ant-modal-body .ant-input-number-input,
          .ant-modal-body .ant-select-selector,
          .ant-modal-body .ant-input-affix-wrapper,
          .ant-modal-body textarea.ant-input {
            border-radius: 16px !important;
          }
          
          .ant-modal-body .ant-select-focused .ant-select-selector {
            border-radius: 16px !important;
          }
          
          .ant-modal-body .ant-input-number {
            border-radius: 16px !important;
          }
          
          .ant-modal-body .ant-input-number-input {
            height: 32px !important;
            line-height: 32px !important;
          }
          
          .ant-modal-body textarea.ant-input {
            height: 32px !important;
            line-height: 32px !important;
            padding-top: 4px !important;
            padding-bottom: 4px !important;
          }
          
          .ant-modal-body .ant-input-number-group .ant-input-number {
            border-radius: 0 !important;
          }
          
          .ant-modal-body .ant-input-number-group-addon:first-child {
            border-radius: 16px 0 0 16px !important;
          }
          
          .ant-modal-body .ant-input-number-group-addon:last-child {
            border-radius: 0 16px 16px 0 !important;
          }
          
          .ant-modal-body .ant-select-selector {
            border-radius: 16px !important;
          }
          
          .ant-modal-body .ant-select-selection-search-input {
            border-radius: 16px !important;
          }
          
          .ant-modal-header .ant-modal-title {
            font-size: 18px !important;
          }
        `}</style>
        {submitError && <AlertError error={submitError} />}
        {loadingRecord ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px', color: theme.colors.grey6 }}>Loading tax configuration...</div>
          </div>
        ) : (
          <Form 
            form={form} 
            layout="vertical"
            onValuesChange={(changedValues, allValues) => {
              if (changedValues.taxName !== undefined) {
                setTaxNameValue(changedValues.taxName || '')
              }
              if (changedValues.taxDisplayName !== undefined) {
                setTaxDisplayNameValue(changedValues.taxDisplayName || '')
              }
              if (changedValues.displayNotes !== undefined) {
                setDisplayNotesValue(changedValues.displayNotes || '')
              }
            }}
          >
          <Row gutter={24} align="top">
            {/* Left Column - Form Inputs */}
            <Col flex="11">
              <div style={{
                maxHeight: 'calc(100vh - 280px)',
                overflowY: 'auto',
                paddingRight: '8px'
              }}>
          <style>{`
            .tax-condition-form-item .ant-form-item-control {
              line-height: 1;
              min-height: auto;
            }
            .tax-condition-form-item .ant-form-item-control-input {
              min-height: auto;
            }
            .tax-condition-form-item .ant-form-item-control-input-content {
              line-height: 1;
            }
          `}</style>
          <Form.Item 
            style={{ marginBottom: '18px' }}
            shouldUpdate={(prevValues, currentValues) => 
            prevValues.taxName !== currentValues.taxName ||
            prevValues.locationSpecification !== currentValues.locationSpecification ||
            prevValues.states !== currentValues.states ||
            prevValues.zipCodes !== currentValues.zipCodes ||
            prevValues.rateValuePerKwh !== currentValues.rateValuePerKwh ||
            prevValues.rateValuePercentage !== currentValues.rateValuePercentage ||
            prevValues.rateType !== currentValues.rateType ||
            prevValues.approvedByName !== currentValues.approvedByName ||
            prevValues.policyApprovalAgreement !== currentValues.policyApprovalAgreement
          }>
            {({ getFieldValue }) => {
              const taxName = getFieldValue('taxName')
              const locationSpecification = getFieldValue('locationSpecification')
              const states = getFieldValue('states')
              const zipCodes = getFieldValue('zipCodes')
              const currentRateType = getFieldValue('rateType') || rateType
              const rateValuePerKwh = getFieldValue('rateValuePerKwh')
              const rateValuePercentage = getFieldValue('rateValuePercentage')
              const approvedByName = getFieldValue('approvedByName')
              const policyApprovalAgreement = getFieldValue('policyApprovalAgreement')

              // Check if location is filled (either states or zipCodes based on locationSpecification)
              const isLocationFilled = locationSpecification === 'byState' 
                ? states && Array.isArray(states) && states.length > 0
                : zipCodes && zipCodes.trim() !== ''

              // Use the appropriate rate value based on rate type
              const rateValue = currentRateType === 'perKwh' ? rateValuePerKwh : rateValuePercentage

              const completedFields = [
                !!taxName,
                isLocationFilled,
                !!rateValue,
                !!approvedByName,
                !!policyApprovalAgreement,
              ].filter(Boolean).length

              const totalFields = 5
              const progressPercent = (completedFields / totalFields) * 100
              const progressText = `${completedFields} of ${totalFields} required fields`

              return (
                <div
                  style={{
                    backgroundColor: theme.colors.grey1,
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                >
                  <span style={{ color: theme.colors.grey7, fontSize: '14px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {formCompletionText}
                  </span>
                  <Progress
                    percent={progressPercent}
                    showInfo={false}
                    strokeColor={theme.colors.neonGreen}
                    style={{ flex: 1, margin: 0 }}
                  />
                  <span style={{ color: theme.colors.grey6, fontSize: '14px', whiteSpace: 'nowrap' }}>
                    {progressText}
                  </span>
                </div>
              )
            }}
          </Form.Item>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ margin: '0 0 8px 0', color: theme.colors.grey7, fontSize: '16px', fontWeight: 600, textAlign: 'left' }}>
              {ruleBasicsText}
            </h3>
            <Divider style={{ margin: 0 }} />
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={taxNameText}
                name="taxName"
                rules={[
                  { required: true, message: 'Please enter tax name' },
                  { max: 50, message: 'Tax name must not exceed 50 characters' }
                ]}
                extra={
                  <span style={{ 
                    fontSize: '12px', 
                    color: (taxNameValue?.length || 0) >= 50 ? theme.colors.red : theme.colors.grey6 
                  }}>
                    {(taxNameValue?.length || 0)}/50
                  </span>
                }
              >
                <Input 
                  placeholder={enterNameText}
                  maxLength={50}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={taxTypeText}
                name="taxType"
                rules={[{ required: true, message: 'Please select tax type' }]}
                initialValue="excise"
              >
                <Select placeholder="Select tax type">
                  <Option value="excise">{exciseTaxText}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={taxDescriptionText} name="taxDescription">
            <Input placeholder={enterDescriptionText} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={countryText}
                name="country"
                rules={[{ required: true, message: 'Please select country' }]}
                initialValue="united-states"
                style={{ marginBottom: '8px' }}
              >
                <Select placeholder="Select country">
                  <Option value="united-states">{unitedStatesText}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={locationSpecificationText}
                name="locationSpecification"
                initialValue="byState"
                rules={[{ required: true, message: 'Please select location specification' }]}
                style={{ marginBottom: '8px' }}
              >
                <Radio.Group
                  value={locationSpecification}
                  onChange={(e) => {
                    setLocationSpecification(e.target.value)
                    form.setFieldsValue({ locationSpecification: e.target.value })
                    // Clear the other field when switching
                    if (e.target.value === 'byState') {
                      form.setFieldsValue({ zipCodes: undefined })
                    } else {
                      form.setFieldsValue({ states: undefined })
                    }
                  }}
                >
                  <Radio value="byState">{byStateText}</Radio>
                  <Radio value="byZipCodes">{byZipCodesText}</Radio>
                </Radio.Group>
              </Form.Item>
              {locationSpecification === 'byState' && (
                <Form.Item
                  label={statesText}
                  name="states"
                  rules={[
                    { required: true, message: 'Please select at least one state' },
                    {
                      validator: (_, value) => {
                        if (!value || value.length === 0) {
                          return Promise.reject(new Error('Please select at least one state'))
                        }
                        return Promise.resolve()
                      },
                    },
                  ]}
                  style={{ marginBottom: '8px' }}
                >
                  <Select
                    mode="multiple"
                    placeholder={selectStatesText}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {STATE_OPTIONS.map((state) => (
                      <Option key={state.value} value={state.value}>
                        {state.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              {locationSpecification === 'byZipCodes' && (
                <Form.Item
                  label={zipCodesText}
                  name="zipCodes"
                  rules={[
                    { required: true, message: 'Please enter at least one ZIP code' },
                    {
                      validator: (_, value) => {
                        if (!value || value.trim() === '') {
                          return Promise.reject(new Error('Please enter at least one ZIP code'))
                        }
                        // Validate ZIP code format (5 digits or 5+4 format)
                        const zipCodes = value.split(',').map((zip: string) => zip.trim()).filter((zip: string) => zip !== '')
                        const zipRegex = /^\d{5}(-\d{4})?$/
                        for (const zip of zipCodes) {
                          if (!zipRegex.test(zip)) {
                            return Promise.reject(new Error(`Invalid ZIP code format: ${zip}. Use format 12345 or 12345-6789`))
                          }
                        }
                        return Promise.resolve()
                      },
                    },
                  ]}
                  help={enterZipCodesText}
                  style={{ marginBottom: '8px' }}
                >
                  <Input.TextArea
                    rows={1}
                    autoSize={{ minRows: 1, maxRows: 1 }}
                    placeholder={enterZipCodesText}
                    style={{ height: '32px', resize: 'none' }}
                    onChange={(e) => {
                      // Auto-format: remove extra spaces and normalize
                      const value = e.target.value
                      form.setFieldsValue({ zipCodes: value })
                    }}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    {taxDisplayNameText}{' '}
                    <Tooltip title={taxDisplayNameTooltipText}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0px', verticalAlign: 'middle' }}>
                        <QuestionIcon color={theme.colors.lightGray} width={14} height={14} />
                      </span>
                    </Tooltip>
                  </span>
                }
                name="taxDisplayName"
                rules={[
                  { required: true, message: 'Please enter a tax display name' },
                  { max: 50, message: 'Tax display name must not exceed 50 characters' }
                ]}
                extra={
                  <span style={{ 
                    fontSize: '12px', 
                    color: (taxDisplayNameValue?.length || 0) >= 50 ? theme.colors.red : theme.colors.grey6 
                  }}>
                    {(taxDisplayNameValue?.length || 0)}/50
                  </span>
                }
              >
                <Input 
                  placeholder={taxDisplayNamePlaceholderText} 
                  maxLength={50}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    {displayNotesText}{' '}
                    <Tooltip title={displayNotesTooltipText}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0px', verticalAlign: 'middle' }}>
                        <QuestionIcon color={theme.colors.lightGray} width={14} height={14} />
                      </span>
                    </Tooltip>
                  </span>
                }
                name="displayNotes"
                rules={[
                  { required: true, message: 'Please enter display notes' },
                  { max: 50, message: 'Display notes must not exceed 50 characters' }
                ]}
                extra={
                  <span style={{ 
                    fontSize: '12px', 
                    color: (displayNotesValue?.length || 0) >= 50 ? theme.colors.red : theme.colors.grey6 
                  }}>
                    {(displayNotesValue?.length || 0)}/50
                  </span>
                }
              >
                <Input 
                  placeholder={displayNotesPlaceholderText} 
                  maxLength={50}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={taxAuthorityContactText} name="taxAuthorityContact">
                <Input placeholder={taxAuthorityContactPlaceholderText} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={regulatoryReferenceText} name="regulatoryReference">
                <Input placeholder={regulatoryReferencePlaceholderText} />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginBottom: '8px', marginTop: '12px' }}>
            <h3 style={{ margin: '0 0 4px 0', color: theme.colors.grey7, fontSize: '16px', fontWeight: 600, textAlign: 'left' }}>
              {taxConditionText}
            </h3>
            <Divider style={{ margin: 0 }} />
          </div>

          <Row gutter={16} align="middle">
            <Col span={12}>
              <Form.Item
                className="tax-condition-form-item"
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Switch
                      checked={powerThresholdEnabled}
                      onChange={(checked) => {
                        setPowerThresholdEnabled(checked)
                        if (!checked) {
                          form.setFieldsValue({ powerThresholdFrom: undefined, powerThresholdTo: undefined })
                        }
                      }}
                    />
                    <span style={{ fontWeight: 600, color: theme.colors.grey7 }}>{powerThresholdText}</span>
                  </div>
                }
                style={{ marginBottom: '4px' }}
              >
                <div />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                className="tax-condition-form-item"
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Switch
                      checked={minimumTransactionAmountEnabled}
                      onChange={(checked) => {
                        setMinimumTransactionAmountEnabled(checked)
                        if (!checked) {
                          form.setFieldsValue({ minimumTransactionAmount: undefined })
                        }
                      }}
                    />
                    <span style={{ fontWeight: 600, color: theme.colors.grey7 }}>{minimumTransactionAmountText}</span>
                  </div>
                }
                style={{ marginBottom: '4px' }}
              >
                <div />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              {powerThresholdEnabled && (
                <>
                  <div style={{ display: 'flex', alignItems: 'left', marginBottom: '4px', marginTop: '8px', width: '100%' }}>
                    <Form.Item name="powerThresholdFrom" style={{ marginBottom: 0, marginRight: '12px', width: '100px' }}>
                      <InputNumber
                        style={{ width: '100%', height: '32px' }}
                        placeholder="0"
                        precision={0}
                        min={0}
                        controls={false}
                        disabled={!powerThresholdEnabled}
                      />
                    </Form.Item>
                    <span style={{ 
                      color: theme.colors.grey6,
                      fontSize: '14px',
                      lineHeight: '32px',
                      height: '32px',
                      display: 'inline-flex',
                      alignItems: 'left',
                      marginRight: '8px'
                    }}>to</span>
                    <Form.Item name="powerThresholdTo" style={{ marginBottom: 0, width: '120px' }}>
                      <InputNumber
                        style={{ width: '100%', height: '32px' }}
                        placeholder="50"
                        precision={0}
                        min={0}
                        controls={false}
                        disabled={!powerThresholdEnabled}
                      />
                    </Form.Item>
                  </div>
                  <div style={{ marginBottom: '8px', marginTop: '2px' }}>
                    <span style={{ fontSize: '12px', color: theme.colors.grey6 }}>{powerThresholdHelperText}</span>
                  </div>
                </>
              )}
            </Col>
            <Col span={12}>
              {minimumTransactionAmountEnabled && (
                <>
                  <Form.Item name="minimumTransactionAmount" style={{ marginBottom: '4px', marginTop: '8px' }}>
                    <InputNumber
                      style={{ width: '50%' }}
                      placeholder="0.00"
                      precision={2}
                      min={0}
                      controls={false}
                      disabled={!minimumTransactionAmountEnabled}
                      addonBefore="$"
                    />
                  </Form.Item>
                  <div style={{ marginBottom: '8px', marginTop: '2px' }}>
                    <span style={{ fontSize: '12px', color: theme.colors.grey6 }}>{minimumTransactionAmountHelperText}</span>
                  </div>
                </>
              )}
            </Col>
          </Row>

          <div style={{ marginBottom: '8px', marginTop: '12px' }}>
            <h3 style={{ margin: '0 0 4px 0', color: theme.colors.grey7, fontSize: '16px', fontWeight: 600, textAlign: 'left' }}>
              {ratesAndCalculationText}
            </h3>
            <Divider style={{ margin: 0 }} />
          </div>

          <Form.Item
            label={rateTypeText}
            name="rateType"
            rules={[{ required: true, message: 'Please select rate type' }]}
            initialValue="perKwh"
            style={{ marginBottom: '8px' }}
          >
            <Radio.Group
              value={rateType}
              onChange={(e) => {
                setRateType(e.target.value)
                form.setFieldsValue({ rateType: e.target.value })
                if (e.target.value === 'percentage') {
                  // Reset free session handling when switching to percentage (0% * any percentage = 0)
                  setApplyTaxToFreeSessions(false)
                  form.setFieldsValue({ applyTaxToFreeSessions: false })
                } else {
                  form.setFieldsValue({ taxAppliesTo: undefined })
                }
              }}
            >
              <Radio value="perKwh">{perKwhDollarText}</Radio>
              <Radio value="percentage">{percentageText}</Radio>
            </Radio.Group>
          </Form.Item>

          {rateType === 'percentage' && (
            <Form.Item
              label={taxAppliesToText}
              name="taxAppliesTo"
              rules={[{ required: true, message: 'Please select what the tax applies to' }]}
              initialValue="energyCostOnly"
              style={{ marginBottom: '8px' }}
            >
              <Radio.Group style={{ display: 'flex', gap: '16px' }}>
                <Radio value="allCost">{allCostText}</Radio>
                <Radio value="energyCostOnly">{energyCostOnlyText}</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          {rateType === 'perKwh' ? (
            <Form.Item
              label={ratePerKwhLabelText}
              name="rateValuePerKwh"
              rules={[{ required: true, message: 'Please enter rate value' }]}
              help={rateValueHelperText}
              style={{ marginBottom: '8px' }}
            >
              <InputNumber
                style={{ width: '42%', height: '32px' }}
                placeholder="0.03"
                precision={6}
                min={0}
                addonBefore="$"
                step={0.000001}
                addonAfter="/ kWh"
              />
            </Form.Item>
          ) : (
            <Form.Item
              label={ratePercentageLabelText}
              name="rateValuePercentage"
              rules={[{ required: true, message: 'Please enter percentage value' }]}
              style={{ marginBottom: '8px' }}
            >
              <InputNumber
                style={{ width: '30%', height: '32px' }}
                placeholder="5"
                precision={4}
                min={0}
                max={100}
                addonAfter="%"
              />
            </Form.Item>
          )}

          {rateType === 'perKwh' && (
            <>
              <Form.Item
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch
                      checked={applyTaxToFreeSessions}
                      onChange={(checked) => {
                        setApplyTaxToFreeSessions(checked)
                        form.setFieldsValue({ applyTaxToFreeSessions: checked })
                      }}
                    />
                    <span style={{ whiteSpace: 'pre-line' }}>{applyTaxToFreeSessionsText}</span>
                    <Tooltip title={applyTaxToFreeSessions ? applyTaxToFreeSessionsHelperTextOn : applyTaxToFreeSessionsHelperTextOff}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '0px', verticalAlign: 'middle' }}>
                        <QuestionIcon color={theme.colors.lightGray} width={14} height={14} />
                      </span>
                    </Tooltip>
                  </div>
                }
                name="applyTaxToFreeSessions"
                valuePropName="checked"
                style={{ marginBottom: '0' }}
              >
                <div />
              </Form.Item>
              <div style={{ marginBottom: '16px', marginTop: '-24px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', color: theme.colors.grey6 }}>
                    {applyTaxToFreeSessions ? applyTaxToFreeSessionsHelperTextOn : applyTaxToFreeSessionsHelperTextOff}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: theme.colors.grey6, fontStyle: 'italic' }}>
                    {applyTaxToFreeSessionsNoteText}
                  </span>
                </div>
              </div>
            </>
          )}

              <div style={{ marginTop: '24px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: theme.colors.grey7, fontSize: '16px', fontWeight: 600, textAlign: 'left' }}>
                    {policyApprovalText}
                  </h3>
                  <Divider style={{ margin: 0 }} />
                </div>

                <Form.Item
                  name="policyApprovalAgreement"
                  valuePropName="checked"
                  rules={[{ required: true, message: 'Please agree to the policy approval' }]}
                >
                  <Checkbox>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: theme.colors.red }}>*</span>
                      <span style={{ fontSize: '12px', fontFamily: theme.fontFamily.regularFontFamily }}>{policyApprovalCheckboxText}</span>
                    </span>
                  </Checkbox>
                </Form.Item>

                <Form.Item
                  label={approverNameText}
                  name="approvedByName"
                  rules={[{ required: true, message: 'Please enter your name' }]}
                >
                  <Input placeholder={enterApproverNameText} style={{ width: '48%' }} />
                </Form.Item>
              </div>
              </div>
            </Col>
            {/* Right Column - Preview */}
            <Col flex="8">
              <div style={{ 
                maxHeight: 'calc(100vh - 280px)',
                overflowY: 'auto',
                paddingRight: '8px'
              }}>
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                alignSelf: 'flex-start'
              }}>
          <Form.Item
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.locationSpecification !== currentValues.locationSpecification ||
              prevValues.states !== currentValues.states ||
              prevValues.zipCodes !== currentValues.zipCodes ||
              prevValues.applyTaxToFreeSessions !== currentValues.applyTaxToFreeSessions ||
              prevValues.taxDisplayName !== currentValues.taxDisplayName ||
              prevValues.displayNotes !== currentValues.displayNotes ||
              prevValues.powerThresholdFrom !== currentValues.powerThresholdFrom ||
              prevValues.powerThresholdTo !== currentValues.powerThresholdTo ||
              prevValues.minimumTransactionAmount !== currentValues.minimumTransactionAmount ||
              prevValues.rateType !== currentValues.rateType ||
              prevValues.rateValuePerKwh !== currentValues.rateValuePerKwh ||
              prevValues.rateValuePercentage !== currentValues.rateValuePercentage ||
              prevValues.taxAppliesTo !== currentValues.taxAppliesTo
            }
            noStyle
          >
            {({ getFieldValue }) => {
              const locationSpecificationValue = getFieldValue('locationSpecification') || 'byState'
              const statesValue = getFieldValue('states')
              const zipCodesValue = getFieldValue('zipCodes')
              const summaryApplyTaxToFreeSessions = getFieldValue('applyTaxToFreeSessions') ?? applyTaxToFreeSessions
              const displayNameValue = getFieldValue('taxDisplayName')
              const displayNotesValue = getFieldValue('displayNotes')
              const powerThresholdFromValue = getFieldValue('powerThresholdFrom')
              const powerThresholdToValue = getFieldValue('powerThresholdTo')
              const minimumTransactionAmountValue = getFieldValue('minimumTransactionAmount')
              const rateTypeValue = (getFieldValue('rateType') || rateType) as 'perKwh' | 'percentage'
              const rateValuePerKwhValue = getFieldValue('rateValuePerKwh') as number | undefined
              const rateValuePercentageValue = getFieldValue('rateValuePercentage') as number | undefined
              const taxAppliesToValue = getFieldValue('taxAppliesTo') || 'energyCostOnly'

              const selectedStateLabels = Array.isArray(statesValue)
                ? statesValue.map((state: string) => STATE_LABEL_MAP[state] ?? state)
                : []

              const formattedZipCodes = zipCodesValue
                ? zipCodesValue
                  .split(',')
                  .map((zip: string) => zip.trim())
                  .filter((zip: string) => zip !== '')
                  .join(', ')
                : ''

              const locationLabel = locationSpecificationValue === 'byState' ? statesText : zipCodesText
              let locationSummary = 'Not specified'
              if (locationSpecificationValue === 'byState' && selectedStateLabels.length > 0) {
                locationSummary = selectedStateLabels.join(', ')
              } else if (locationSpecificationValue === 'byZipCodes' && formattedZipCodes) {
                locationSummary = formattedZipCodes
              }

              const previewDisplayName = displayNameValue || taxDisplayNamePlaceholderText
              const previewDisplayNotes = displayNotesValue || displayNotesPlaceholderText
              const powerThresholdSummary = powerThresholdEnabled
                ? `Power threshold: ${powerThresholdFromValue ?? 0} - ${powerThresholdToValue ?? 0} kW`
                : noPowerThresholdText
              const minimumTransactionAmountNumber = Number(minimumTransactionAmountValue ?? 0)
              const minimumTransactionSummary = minimumTransactionAmountEnabled
                ? `Minimum transaction amount: $${minimumTransactionAmountNumber.toFixed(2)}`
                : noMinimumTransactionAmountText
              const summaryRateLabel = rateTypeValue === 'perKwh' ? ratePerKwhLabelText : ratePercentageLabelText
              const rateSummaryText = formatRateValue(rateTypeValue, rateValuePerKwhValue, rateValuePercentageValue)
              const taxAppliesToSummary = taxAppliesToValue === 'allCost' ? allCostText : energyCostOnlyText

              return (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '16px' }}>
                  <Card
                    style={{
                      backgroundColor: theme.colors.grey1,
                      borderRadius: '8px',
                      border: 'none',
                      flex: '0 0 auto',
                    }}
                    bodyStyle={{ padding: '16px' }}
                  >
                    <h3 style={{ margin: '0 0 16px 0', color: theme.colors.grey7, fontSize: '16px', fontWeight: 600 }}>
                      {ruleSummaryText}
                    </h3>
                    <div style={{ marginBottom: '16px' }}>
                      <span style={{ color: theme.colors.grey7, fontSize: '12px' }}>
                        {locationLabel}: {locationSummary}
                      </span>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <span style={{ color: theme.colors.grey7, fontSize: '12px' }}>
                        {summaryRateLabel}: {rateSummaryText}
                      </span>
                    </div>
                    {rateTypeValue === 'percentage' && (
                      <div style={{ marginBottom: '16px' }}>
                        <span style={{ color: theme.colors.grey7, fontSize: '12px' }}>
                          {taxAppliesToText}: {taxAppliesToSummary}
                        </span>
                      </div>
                    )}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ marginBottom: '8px', color: theme.colors.grey7, fontSize: '12px', fontWeight: 600 }}>
                        {taxConditionsText}:
                      </div>
                      <ul style={{ margin: '0', paddingLeft: '20px', color: theme.colors.grey7, fontSize: '12px', lineHeight: '1.8' }}>
                        <li style={{ marginBottom: '4px' }}>{powerThresholdSummary}</li>
                        <li style={{ marginBottom: '4px' }}>
                          {summaryApplyTaxToFreeSessions ? taxAppliesToFreeSessionsText : noTaxOnFreeSessionsText}
                        </li>
                        <li>{minimumTransactionSummary}</li>
                      </ul>
                    </div>
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${theme.colors.grey2}` }}>
                      <span style={{ fontSize: '12px', color: theme.colors.black, fontStyle: 'italic' }}>
                        {configureTaxRuleText}
                      </span>
                    </div>
                  </Card>
                  <Card
                    style={{
                      backgroundColor: theme.colors.grey1,
                      borderRadius: '8px',
                      border: 'none',
                      flex: '0 1 auto',
                      display: 'flex',
                      flexDirection: 'column',
                      marginBottom: '0',
                    }}
                    bodyStyle={{ padding: '16px', display: 'flex', flexDirection: 'column', paddingBottom: '16px' }}
                  >
                    <h3 style={{ margin: '0 0 16px 0', color: theme.colors.grey7, fontSize: '16px', fontWeight: 600 }}>
                      {previewText}
                    </h3>
                    <Alert
                      message={previewAlertText}
                      type="info"
                      icon={<InfoCircleOutlined style={{ color: '#fa8c16' }} />}
                      style={{
                        marginBottom: '16px',
                        backgroundColor: '#fffbe6',
                        border: '1px solid #ffe58f',
                        borderRadius: '4px',
                      }}
                    />
                    <div style={{ borderRadius: '4px', overflow: 'hidden', backgroundColor: theme.colors.white }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 16px' }}>
                        <span style={{ color: theme.colors.grey7, fontSize: '12px' }}>{chargeAmountText}</span>
                        <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 500 }}>$14.34 USD</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 16px' }}>
                        <span style={{ color: theme.colors.grey7, fontSize: '12px' }}>{subTotalText}</span>
                        <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 500 }}>$14.34 USD</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 16px' }}>
                        <span style={{ color: theme.colors.grey7, fontSize: '12px' }}>
                          {localTaxText} <span style={{ color: theme.colors.grey6, fontSize: '12px', fontWeight: 400 }}>({autoText})</span>
                        </span>
                        <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 500 }}>$0.89 USD</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 16px' }}>
                        <span style={{ color: theme.colors.grey7, fontSize: '12px' }}>
                          {taxText} <span style={{ color: theme.colors.grey6, fontSize: '12px', fontWeight: 400 }}>({autoText})</span>
                        </span>
                        <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 500 }}>$0.00 USD</span>
                      </div>
                      <div style={{ border: `1px solid ${theme.colors.grey2}`, borderRadius: '4px', overflow: 'hidden', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '6px 16px', backgroundColor: '#fffbe6', marginTop: '8px', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                          <span style={{ color: theme.colors.softBlack, fontSize: '12px', fontWeight: 600 }}>
                            {previewDisplayName}
                          </span>
                          <span style={{ color: theme.colors.grey6, fontSize: '12px' }}>
                            {previewDisplayNotes}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                          <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 500 }}>$0.00 USD</span>
                          <span style={{ height: '12px' }}></span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 16px', backgroundColor: theme.colors.grey1 }}>
                        <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 600 }}>{totalText}</span>
                        <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 600 }}>$15.23 USD</span>
                      </div>
                    </div>
                  </Card>
                </div>
              )
            }}
          </Form.Item>
              </div>
              </div>
            </Col>
          </Row>
        </Form>
        )}
      </Modal>
      <Modal
        title="Confirm Save and Activate"
        visible={isConfirmModalOpen}
        onCancel={() => setIsConfirmModalOpen(false)}
        footer={null}
      >
        <p>Are you sure you want to save and activate this tax configuration?</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
          <Button
            onClick={() => setIsConfirmModalOpen(false)}
            style={{
              backgroundColor: theme.colors.grey7,
              borderColor: theme.colors.grey7,
              color: theme.colors.white,
            }}
          >
            CANCEL
          </Button>
          <Button
            type="primary"
            onClick={handleConfirmAndSubmit}
            loading={submitting}
            style={{
              backgroundColor: theme.colors.neonGreen,
              borderColor: theme.colors.neonGreen,
            }}
          >
            YES, SAVE AND ACTIVATE
          </Button>
        </div>
      </Modal>
      <Modal
        title="Confirm Archive"
        visible={isArchiveConfirmModalOpen}
        onCancel={() => {
          setIsArchiveConfirmModalOpen(false)
          setRecordToArchive(null)
        }}
        footer={null}
      >
        <p>
          Are you sure you want to archive <strong>{recordToArchive?.name}</strong>? Once archived, it will no longer be available to apply to pricing plans. All pricing plans with this active tax rule must be deactivated before proceeding.
        </p>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
          <Button
            onClick={() => {
              setIsArchiveConfirmModalOpen(false)
              setRecordToArchive(null)
            }}
            style={{
              backgroundColor: theme.colors.grey7,
              borderColor: theme.colors.grey7,
              color: theme.colors.white,
            }}
          >
            CANCEL
          </Button>
          <Button
            type="primary"
            onClick={handleConfirmArchive}
            loading={submitting}
            style={{
              backgroundColor: theme.colors.neonGreen,
              borderColor: theme.colors.neonGreen,
            }}
          >
            YES, ARCHIVE
          </Button>
        </div>
      </Modal>
      <Modal
        title="View Tax Rule"
        visible={isViewModalOpen}
        onCancel={() => {
          setIsViewModalOpen(false)
          setRecordToView(null)
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsViewModalOpen(false)
              setRecordToView(null)
            }}
            style={{
              backgroundColor: theme.colors.grey7,
              borderColor: theme.colors.grey7,
              color: theme.colors.white,
            }}
          >
            CLOSE
          </Button>,
        ]}
        width="50%"
      >
        {loadingRecord ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px', color: theme.colors.grey6 }}>Loading tax configuration...</div>
          </div>
        ) : recordToView && (recordToView as any).fullDetails ? (
          <Row gutter={24} align="top">
            <Col span={14}>
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: theme.colors.grey7, fontSize: '16px', fontWeight: 600 }}>
                    {ruleBasicsText}
                  </h3>
                  <Divider style={{ margin: 0 }} />
                </div>
                
                <Row gutter={16} style={{ marginBottom: '16px' }}>
                  <Col span={12}>
                    <div>
                      <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{taxNameText}</div>
                      <div style={{ color: theme.colors.grey7, fontSize: '14px', fontWeight: 500 }}>{recordToView.name}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div>
                      <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{taxTypeText}</div>
                      <div style={{ color: theme.colors.grey7, fontSize: '14px', fontWeight: 500 }}>{exciseTaxText}</div>
                    </div>
                  </Col>
                </Row>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{taxDescriptionText}</div>
                  <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>{(recordToView as any).fullDetails.taxDescription || '-'}</div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{countryText}</div>
                  <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>{unitedStatesText}</div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{locationSpecificationText}</div>
                  <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>
                    {(recordToView as any).fullDetails.states && (recordToView as any).fullDetails.states.length > 0 ? byStateText : byZipCodesText}
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>
                    {(recordToView as any).fullDetails.states && (recordToView as any).fullDetails.states.length > 0 ? statesText : zipCodesText}
                  </div>
                  <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>
                    {(recordToView as any).fullDetails.states && (recordToView as any).fullDetails.states.length > 0
                      ? (recordToView as any).fullDetails.states.map((code: string) => STATE_LABEL_MAP[code] ?? code).join(', ')
                      : (recordToView as any).fullDetails.zipCodes?.join(', ') || '-'}
                  </div>
                </div>

                <Row gutter={16} style={{ marginBottom: '16px' }}>
                  <Col span={12}>
                    <div>
                      <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{taxDisplayNameText}</div>
                      <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>{(recordToView as any).fullDetails.taxNameDisplay || '-'}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div>
                      <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{displayNotesText}</div>
                      <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>{(recordToView as any).fullDetails.taxNotesDisplay || '-'}</div>
                    </div>
                  </Col>
                </Row>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{taxAuthorityContactText}</div>
                  <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>{(recordToView as any).fullDetails.taxAuthorityContact || '-'}</div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{regulatoryReferenceText}</div>
                  <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>{(recordToView as any).fullDetails.regulatoryReference || '-'}</div>
                </div>

                <div style={{ marginTop: '24px', marginBottom: '16px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: theme.colors.grey7, fontSize: '16px', fontWeight: 600 }}>
                    {taxConditionText}
                  </h3>
                  <Divider style={{ margin: 0 }} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{powerThresholdText}</div>
                  <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>{getPowerRangeForRecord(recordToView)}</div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{minimumTransactionAmountText}</div>
                  <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>{getMinimumTransactionAmountForRecord(recordToView)}</div>
                </div>

                <div style={{ marginTop: '24px', marginBottom: '16px' }}>
                  <h3 style={{ margin: '0 0 8px 0', color: theme.colors.grey7, fontSize: '16px', fontWeight: 600 }}>
                    {ratesAndCalculationText}
                  </h3>
                  <Divider style={{ margin: 0 }} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{rateTypeText}</div>
                  <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>
                    {recordToView.rateType === 'perKwh' ? perKwhDollarText : percentageText}
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>
                    {recordToView.rateType === 'perKwh' ? ratePerKwhLabelText : ratePercentageLabelText}
                  </div>
                  <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>
                    {formatRateValue(recordToView.rateType, recordToView.rateCents, recordToView.ratePercentage)}
                  </div>
                </div>

                {recordToView.rateType === 'percentage' && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{taxAppliesToText}</div>
                    <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>
                      {recordToView.appliesTo === 'allCost' ? allCostText : energyCostOnlyText}
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px', whiteSpace: 'pre-line' }}>{applyTaxToFreeSessionsText}</div>
                  <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>{getFreeSessionExemptForRecord(recordToView)}</div>
                </div>

                <div style={{ marginTop: '24px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <h3 style={{ margin: '0 0 8px 0', color: theme.colors.grey7, fontSize: '16px', fontWeight: 600 }}>
                      {policyApprovalText}
                    </h3>
                    <Divider style={{ margin: 0 }} />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ color: theme.colors.grey6, fontSize: '12px', marginBottom: '4px' }}>{approverNameText}</div>
                    <div style={{ color: theme.colors.grey7, fontSize: '14px' }}>{(recordToView as any).fullDetails.approvedByName || '-'}</div>
                  </div>
                </div>
              </div>
            </Col>
            <Col span={10}>
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                paddingRight: '8px',
                alignSelf: 'flex-start'
              }}>
                <Card
                  style={{
                    backgroundColor: theme.colors.grey1,
                    borderRadius: '8px',
                    border: 'none',
                    flex: '0 0 auto',
                  }}
                  bodyStyle={{ padding: '16px' }}
                >
                  <h3 style={{ margin: '0 0 16px 0', color: theme.colors.grey7, fontSize: '16px', fontWeight: 600 }}>
                    {ruleSummaryText}
                  </h3>
                  <div style={{ marginBottom: '16px' }}>
                    <span style={{ color: theme.colors.grey7, fontSize: '14px' }}>
                      {getLocationLabelForRecord(recordToView)}: {getLocationDetailsForRecord(recordToView)}
                    </span>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <span style={{ color: theme.colors.grey7, fontSize: '14px' }}>
                      {getRateLabelForRecord(recordToView)}: {formatRateValue(recordToView.rateType, recordToView.rateCents, recordToView.ratePercentage)}
                    </span>
                  </div>
                  {recordToView.rateType === 'percentage' && (
                    <div style={{ marginBottom: '16px' }}>
                      <span style={{ color: theme.colors.grey7, fontSize: '14px' }}>
                        {taxAppliesToText}: {recordToView.appliesTo === 'allCost' ? allCostText : energyCostOnlyText}
                      </span>
                    </div>
                  )}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px', color: theme.colors.grey7, fontSize: '14px', fontWeight: 600 }}>
                      {taxConditionsText}:
                    </div>
                    <ul style={{ margin: '0', paddingLeft: '20px', color: theme.colors.grey7, fontSize: '14px', lineHeight: '1.8' }}>
                      <li style={{ marginBottom: '4px' }}>{getPowerRangeForRecord(recordToView)}</li>
                      <li style={{ marginBottom: '4px' }}>{getFreeSessionExemptForRecord(recordToView)}</li>
                      <li>{getMinimumTransactionAmountForRecord(recordToView)}</li>
                    </ul>
                  </div>
                </Card>
                <Card
                  style={{
                    backgroundColor: theme.colors.grey1,
                    borderRadius: '8px',
                    border: 'none',
                    flex: '1 1 auto',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  bodyStyle={{ padding: '16px', display: 'flex', flexDirection: 'column', height: '100%' }}
                >
                  <h3 style={{ margin: '0 0 16px 0', color: theme.colors.grey7, fontSize: '16px', fontWeight: 600 }}>
                    {previewText}
                  </h3>
                  <Alert
                    message={previewAlertText}
                    type="info"
                    icon={<InfoCircleOutlined style={{ color: '#fa8c16' }} />}
                    style={{
                      marginBottom: '16px',
                      backgroundColor: '#fffbe6',
                      border: '1px solid #ffe58f',
                      borderRadius: '4px',
                    }}
                  />
                  <div style={{ borderRadius: '4px', overflow: 'hidden', backgroundColor: theme.colors.white }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 16px' }}>
                      <span style={{ color: theme.colors.grey7, fontSize: '12px' }}>{chargeAmountText}</span>
                      <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 500 }}>$14.34 USD</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 16px' }}>
                      <span style={{ color: theme.colors.grey7, fontSize: '12px' }}>{subTotalText}</span>
                      <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 500 }}>$14.34 USD</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 16px' }}>
                      <span style={{ color: theme.colors.grey7, fontSize: '12px' }}>
                        {localTaxText} <span style={{ color: theme.colors.grey6, fontSize: '12px', fontWeight: 400 }}>({autoText})</span>
                      </span>
                      <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 500 }}>$0.89 USD</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 16px' }}>
                      <span style={{ color: theme.colors.grey7, fontSize: '12px' }}>
                        {taxText} <span style={{ color: theme.colors.grey6, fontSize: '12px', fontWeight: 400 }}>({autoText})</span>
                      </span>
                      <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 500 }}>$0.00 USD</span>
                    </div>
                    <div style={{ border: `1px solid ${theme.colors.grey2}`, borderRadius: '4px', overflow: 'hidden', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '6px 16px', backgroundColor: '#fffbe6', marginTop: '8px', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                        <span style={{ color: theme.colors.softBlack, fontSize: '12px', fontWeight: 600 }}>
                          {(recordToView as any).fullDetails.taxNameDisplay || taxDisplayNamePlaceholderText}
                        </span>
                        <span style={{ color: theme.colors.grey6, fontSize: '12px' }}>
                          {(recordToView as any).fullDetails.taxNotesDisplay || displayNotesPlaceholderText}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 500 }}>$0.00 USD</span>
                        <span style={{ height: '12px' }}></span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 16px', backgroundColor: theme.colors.grey1 }}>
                      <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 600 }}>{totalText}</span>
                      <span style={{ color: theme.colors.grey7, fontSize: '12px', fontWeight: 600 }}>$15.23 USD</span>
                    </div>
                  </div>
                </Card>
              </div>
            </Col>
          </Row>
        ) : null}
      </Modal>
        </TabPane>
      </Tabs>
    </BackgroundContainer>
  )
}

export const TaxPage = withAuthenticatedLayout(TaxBasePage)
